import { CommandEngine, type CommandDescriptor, type RunnerState } from './commandengine.js'
import type {
  CommandAvailability,
  CommandAvailabilityState,
  PickerOptionAvailability,
  PickerOptionAvailabilityState
} from './lib/commandAvailability.js'
import { isDetachedSpace, type PaletteKind, type SpaceKind } from './lib/contextModel.js'
import type { OrbitalVisualState } from './lib/runtimePolicy.js'
import { selectPillCommandPolicy } from './lib/surfaceMatrix.js'

// Command system follows completed-feature-PRD-command-architecture.md (chronological runner tokens, forward-only flow)
export type CommandKind = 'oneshot' | 'mode'
export type CommandMode = 'edit' | 'view'

export interface Command {
  name: string
  description: string | (() => string)
  gatedReason?: string | (() => string | undefined)
  availability?: CommandAvailabilityState | CommandAvailability | (() => CommandAvailabilityState | CommandAvailability)
  execute: () => void | Promise<void>
  type?: CommandKind
  ready?: boolean | (() => boolean)
  visible?: boolean | (() => boolean)
  autoClose?: boolean
  modes?: CommandMode[]
}

type SlotOption = {
  label: string
  value: string
  group?: string
  description?: string
  gatedReason?: string
  state?: PickerOptionAvailabilityState
  disabled?: boolean
}

type SlotPicker = {
  label: string
  header?: string
  options: SlotOption[]
  onSelect: (value: string) => void | Promise<void>
  onConfirm?: () => void | Promise<void>
  onHighlight?: (value: string) => void
  closeOnSelect?: boolean
  previewOnHighlight?: boolean
}

function compareCommandListLabels(a: string, b: string): number {
  return a.localeCompare(b)
}

function sortCommandListValues(values: readonly string[]): string[] {
  return [...values].sort(compareCommandListLabels)
}

function sortCommandListItems<T>(items: readonly T[], getLabel: (item: T) => string): T[] {
  return [...items].sort((a, b) => compareCommandListLabels(getLabel(a), getLabel(b)))
}

export type SyncVisualState =
  | 'idle-synced'
  | 'idle-disabled'
  | 'pending'
  | 'syncing'
  | 'success'
  | 'blocked'

export type SyncBentoState = {
  userLabel: string
  userTone?: 'neutral' | 'sync' | 'offline'
  syncLabel: string
  failedAuth?: boolean
  networkLabel?: string
  syncDetail?: string
  feedback?: { message: string, tone?: 'success' | 'error' }
  description?: string
  syncPickerPlaceholder?: string
  cloudActions?: string[]
  resolveCloudActionAvailability?: (action: string) => PickerOptionAvailability
  onCloudAction?: (action: string) => void | Promise<void>
  onUserAction?: (action?: string) => void | Promise<void>
  isUserActionEnabled?: (action: string) => boolean
}

type AccountActionViewModel = {
  action: 'login' | 'logout'
  enabled: boolean
}

// Notice contract: a system-initiated, non-blocking message between the app
// and the user. Not a command (no user intent) and not a modal (never
// blocks). Closing the surface counts as picking the first quiet choice.
type NoticeChoice = {
  label: string
  tone: 'armed' | 'quiet'
  onPick: () => void | Promise<void>
}

type NoticeState = {
  feedback: string
  copy: string
  choices: NoticeChoice[]
}

type RunnerShellRefs = {
  promptButton: HTMLButtonElement
  tokensEl: HTMLSpanElement
  feedbackEl: HTMLSpanElement
  descriptionBodyEl: HTMLDivElement
  descriptionCopyEl: HTMLSpanElement
}

export type IndexPaletteStatus = 'blank' | 'empty' | 'unavailable'
export type IndexPaletteRow = {
  id: string
  label: string
  description?: string
  selected?: boolean
  current?: boolean
  statusTone?: 'neutral' | 'sync' | 'offline'
}

export type IndexPaletteState = {
  status: IndexPaletteStatus
  title?: string
  description?: string
  rows?: IndexPaletteRow[]
  actions?: string[]
  resolveActionAvailability?: (action: string) => PickerOptionAvailability
  onAction?: (action: string) => void | Promise<void>
  onSelectRow?: (rowId: string) => void | Promise<void>
  onOpenRow?: (rowId: string) => void | Promise<void>
}

type PaletteOverlay = 'collapsed' | 'expanded'

type PaletteLayoutState = {
  space: SpaceKind
  palette: PaletteKind
  overlay: PaletteOverlay
}

type PaletteHideReason = 'outside-tap' | 'command' | 'programmatic'

type PaletteBentoViewModel = {
  top: 'account' | 'sync-bento' | 'none'
  middle: 'wheel' | 'index-stack' | 'none'
  bottom: 'runner' | 'notice' | 'none'
}

const STATIC_LEGAL_LINKS = [
  { label: 'privacy', href: '/privacy.html' },
  { label: 'terms', href: '/terms.html' },
  { label: 'impressum', href: '/impressum.html' }
] as const

export class Commands {
  private static readonly EXECUTION_PRESS_DELAY_MS = 40
  private commands = new Map<string, Command>()
  private sortedCommands: Command[] = []
  private currentMode: CommandMode = 'edit'
  private pickCommand: Command = {
    name: 'pick/',
    description: 'Select a command and hit go',
    execute: () => {}
  }
  private palette?: HTMLDivElement
  private isVisible = false
  private outsideHandler?: (e: Event) => void
  private onHideCallback?: (reason: PaletteHideReason) => void
  private onSurfaceChangeCallback?: (surface: 'go' | 'sync' | 'index' | 'update') => void
  private isTouchDevice: boolean
  private wheelEl?: HTMLDivElement
  private wheelItems: HTMLDivElement[] = []
  private indexListEl?: HTMLDivElement
  private indexListItems: HTMLDivElement[] = []
  private indexListSelectedIndex = -1
  private indexListScrollReleaseTimer?: number
  private promptButton?: HTMLButtonElement
  private promptLabelEl?: HTMLSpanElement
  private promptCommandEl?: HTMLSpanElement
  private promptStepEl?: HTMLSpanElement
  private promptFeedbackEl?: HTMLSpanElement
  private promptDescriptionBodyEl?: HTMLDivElement
  private descriptionNameEl?: HTMLElement
  private descriptionDashEl?: HTMLElement
  private descriptionCopyEl?: HTMLElement
  private selectedIndex = 0
  private lastHighlightedSlotIndex = -1
  private lastUsedCommandName?: string
  private rowHeight = 0
  private wheelRaf: number | null = null
  private wheelMode: 'commands' | 'slot' = 'commands'
  private slotPicker?: SlotPicker
  private slotPickerTapValue?: string
  private mode?: { name: string, message: string, label?: string, activeTokens?: string[] }
  private modeExitHandler?: (modeName: string) => void
  private modeTokenHandler?: (token: string) => void
  private suppressNextToggle = false
  private suppressScrollSync = false
  private scrollSyncReleaseTimer?: number
  private engine?: CommandEngine
  private engineActive = false
  private engineCanvasMode = false
  private engineRunnerState?: RunnerState
  private engineHasDone = false
  private engineTokenSignature?: string
  private engineExitHandler?: () => void
  private accountBar?: HTMLDivElement
  private accountUserLabel = 'user : anonymous | login'
  private accountSpaceLabel = 'space : local'
  private accountUserTone: 'neutral' | 'sync' | 'offline' = 'neutral'
  private accountSpaceTone: 'neutral' | 'sync' | 'offline' = 'neutral'
  private accountUserHandler?: (action?: string) => void
  private accountSpaceHandler?: (action?: string) => void
  private accountUserActionEnabled?: (action: string) => boolean
  private accountSpaceActionEnabled?: (action: string) => boolean
  private syncIndicator?: HTMLDivElement
  private indexIndicator?: HTMLDivElement
  private syncClickHandler?: () => void
  private indexClickHandler?: () => void
  private memoryActivityKey?: number
  private surface: 'go' | 'sync' | 'index' | 'update' = 'go'
  private spaceKind: SpaceKind = 'authored'
  private syncBentoState: SyncBentoState = {
    userLabel: 'user : anonymous',
    userTone: 'neutral',
    syncLabel: 'sync : offline',
    description: ''
  }
  private noticeState?: NoticeState
  private indexPaletteState: IndexPaletteState = {
    status: 'blank',
    rows: [],
    actions: []
  }
  private displayModeMql?: MediaQueryList
  private readonly applyDisplayModeClassHandler = () => this.applyDisplayModeClass()
  private clearOutsideHandler(): void {
    if (this.outsideHandler) {
      document.removeEventListener('pointerdown', this.outsideHandler, true)
      this.outsideHandler = undefined
    }
  }

  private pulseExecutionPress(el: HTMLElement): void {
    el.classList.add('is-fire-pressed')
    window.setTimeout(() => {
      el.classList.remove('is-fire-pressed')
    }, 120)
  }

  private pressThen(el: HTMLElement, action: () => void | Promise<void>): void {
    this.pulseExecutionPress(el)
    window.setTimeout(() => {
      void action()
    }, Commands.EXECUTION_PRESS_DELAY_MS)
  }

  constructor() {
    this.isTouchDevice = (navigator.maxTouchPoints || 0) > 0
    this.createPalette()
    this.setupDisplayModeTracking()
    this.setupKeyboardShortcuts()
    this.engine = new CommandEngine({
      onRunnerUpdate: (state) => {
        this.engineRunnerState = state
        this.engineHasDone = state.tokens.some(token => token.kind === 'done' && token.active)
        this.renderEngineRunnerState(state)
      },
      onOpenPicker: (opts) => {
        this.engineCanvasMode = false
        this.openPickerStep({
          label: opts.label,
          options: opts.options,
          defaultValue: opts.defaultValue,
          onSelect: opts.onSelect,
          onConfirm: opts.onConfirm,
          onHighlight: opts.onHighlight
        })
      },
      onClosePicker: () => {
        this.closeSlotPicker({ minimize: true })
      },
      onEnterCanvasMode: () => {
        this.engineCanvasMode = true
        if (!this.slotPicker) {
          this.closeSlotPicker({ minimize: false })
        }
      },
      onExitCanvasMode: () => {
        this.engineCanvasMode = false
      },
      onComplete: (opts) => {
        this.engineActive = false
        this.engineCanvasMode = false
        this.engineRunnerState = undefined
        this.engineHasDone = false
        this.engineTokenSignature = undefined
        this.engineExitHandler?.()
        const completionUi = opts?.completionUi ?? 'close'
        if (completionUi === 'runner') {
          if (!this.isVisible) {
            this.showPalette()
          } else {
            this.renderPaletteContents()
          }
          this.minimizePalette()
          return
        }
        this.hidePalette()
      }
    })
  }

  private isStandaloneDisplayMode(): boolean {
    const standaloneMedia = typeof window.matchMedia === 'function'
      && window.matchMedia('(display-mode: standalone)').matches
    const iosStandalone = (navigator as any).standalone === true
    return standaloneMedia || iosStandalone
  }

  private applyDisplayModeClass(): void {
    if (!this.palette) return
    const standalone = this.isStandaloneDisplayMode()
    this.palette.classList.toggle('is-standalone', standalone)
    this.palette.classList.toggle('is-browser', !standalone)
  }

  private setupDisplayModeTracking(): void {
    this.applyDisplayModeClass()
    if (typeof window.matchMedia === 'function') {
      this.displayModeMql = window.matchMedia('(display-mode: standalone)')
      const mql = this.displayModeMql as any
      if (typeof mql.addEventListener === 'function') {
        mql.addEventListener('change', this.applyDisplayModeClassHandler)
      } else if (typeof mql.addListener === 'function') {
        mql.addListener(this.applyDisplayModeClassHandler)
      }
    }
    window.addEventListener('pageshow', this.applyDisplayModeClassHandler)
    window.addEventListener('resize', this.applyDisplayModeClassHandler, { passive: true })
  }

  registerCommand(command: Command): void {
    this.commands.set(command.name, command)
    this.resortCommands()
    if (this.isVisible) this.renderPaletteContents()
  }

  setMode(mode: CommandMode): void {
    if (this.currentMode === mode) return
    this.currentMode = mode
    this.resortCommands()
    if (this.isVisible) this.renderPaletteContents()
  }
  
  setOnHideCallback(callback: (reason: PaletteHideReason) => void): void {
    this.onHideCallback = callback
  }

  setOnSurfaceChangeCallback(callback?: (surface: 'go' | 'sync' | 'index' | 'update') => void): void {
    this.onSurfaceChangeCallback = callback
  }

  setEngineExitHandler(handler?: () => void): void {
    this.engineExitHandler = handler
  }

  setModeExitHandler(handler: (modeName: string) => void): void {
    this.modeExitHandler = handler
  }

  triggerModeExit(modeName: string): void {
    if (this.modeExitHandler) {
      this.modeExitHandler(modeName)
    }
  }

  setModeTokenHandler(handler?: (token: string) => void): void {
    this.modeTokenHandler = handler
  }

  setAccountBar(opts: {
    userLabel: string
    spaceLabel: string
    userTone?: 'neutral' | 'sync' | 'offline'
    spaceTone?: 'neutral' | 'sync' | 'offline'
    onUserClick?: (action?: string) => void
    onSpaceClick?: (action?: string) => void
    isUserActionEnabled?: (action: string) => boolean
    isSpaceActionEnabled?: (action: string) => boolean
  }): void {
    this.accountUserLabel = opts.userLabel
    this.accountSpaceLabel = opts.spaceLabel
    this.accountUserTone = opts.userTone ?? 'neutral'
    this.accountSpaceTone = opts.spaceTone ?? 'neutral'
    this.accountUserHandler = opts.onUserClick
    this.accountSpaceHandler = opts.onSpaceClick
    this.accountUserActionEnabled = opts.isUserActionEnabled
    this.accountSpaceActionEnabled = opts.isSpaceActionEnabled
    this.updateAccountBar()
  }

  setSyncIndicatorOnClick(handler?: () => void): void {
    this.syncClickHandler = handler
  }

  setIndexIndicatorOnClick(handler?: () => void): void {
    this.indexClickHandler = handler
  }

  setSyncBentoState(state: SyncBentoState): void {
    this.syncBentoState = { ...state }
    this.palette?.classList.toggle('is-auth-failed', state.failedAuth === true)
    if (this.isVisible && this.surface === 'sync') {
      this.renderPaletteContents()
    }
  }

  setIndexPaletteState(state: IndexPaletteState): void {
    const selectedRowId = this.indexPaletteState.rows?.[this.indexListSelectedIndex]?.id
    // Preserve browsing position on refresh, not the empty list's initial position.
    const scrollTop = this.indexListItems.length ? this.indexListEl?.scrollTop : undefined
    const focusedRowId = this.indexListEl?.contains(document.activeElement)
      ? (document.activeElement as HTMLElement).closest<HTMLElement>('[data-row-id]')?.dataset.rowId
      : undefined
    this.indexPaletteState = { ...state }
    if (this.isVisible && this.surface === 'index' && !this.engineActive) {
      this.renderPaletteContents()
      const selectedIndex = selectedRowId
        ? (this.indexPaletteState.rows ?? []).findIndex(row => row.id === selectedRowId)
        : -1
      if (selectedIndex !== -1) {
        this.setIndexListSelectedIndex(selectedIndex)
      }
      if (focusedRowId) {
        this.indexListItems.find(item => item.dataset.rowId === focusedRowId)
          ?.focus()
      }
      if (scrollTop !== undefined && this.indexListEl) {
        this.indexListEl.scrollTop = scrollTop
      }
    }
  }

  refreshVisibleSurface(): void {
    if (!this.isVisible) return
    this.renderPaletteContents()
  }

  private resetInteractiveModesForTransition(): void {
    if (this.engineActive) {
      this.exitEngine()
    }
    if (this.mode) {
      this.mode = undefined
      this.modeTokenHandler = undefined
    }
  }

  private setSurface(surface: 'go' | 'sync' | 'index' | 'update'): void {
    if (this.surface === surface) return
    this.surface = surface
    this.onSurfaceChangeCallback?.(surface)
  }

  private applyPaletteTransition(opts: {
    overlay: PaletteOverlay
    affordance?: 'go' | 'sync' | 'index' | 'update'
    denyGoInIndex?: boolean
    resetGoPicker?: boolean
  }): void {
    if (opts.overlay === 'collapsed') {
      this.hidePalette()
      return
    }
    const nextSurface = opts.affordance ?? this.surface
    this.resetInteractiveModesForTransition()
    if (nextSurface === 'go' && opts.denyGoInIndex && isDetachedSpace(this.spaceKind)) {
      if (this.isVisible && this.surface !== 'sync') {
        this.hidePalette()
      }
      return
    }
    this.setSurface(nextSurface)
    if (nextSurface === 'go' && opts.resetGoPicker !== false) {
      this.slotPicker = undefined
      this.wheelMode = 'commands'
    }
    if (!this.isVisible) {
      this.showPalette()
      return
    }
    this.renderPaletteContents()
  }

  openSyncSurface(): void {
    this.applyPaletteTransition({
      overlay: 'expanded',
      affordance: 'sync'
    })
  }

  openIndexSurface(): void {
    this.applyPaletteTransition({
      overlay: 'expanded',
      affordance: 'index'
    })
  }

  setNoticeState(state?: NoticeState): void {
    this.noticeState = state
    if (this.isVisible && this.surface === 'update') {
      this.renderPaletteContents()
    }
  }

  openNoticeSurface(): void {
    // The single notice today is the app update; it rides the 'update'
    // palette kind until a second notice member forces a generic surface.
    this.applyPaletteTransition({
      overlay: 'expanded',
      affordance: 'update'
    })
  }

  openGoSurface(): void {
    this.applyPaletteTransition({
      overlay: 'expanded',
      affordance: 'go',
      denyGoInIndex: true,
      resetGoPicker: true
    })
  }

  isSyncSurfaceActive(): boolean {
    return this.surface === 'sync'
  }

  isIndexSurfaceActive(): boolean {
    return this.surface === 'index'
  }

  runDescriptor(command: CommandDescriptor, opts?: { initialValues?: Record<string, string | string[]>, stepIndex?: number }): void {
    if (!this.engine) return
    this.engineActive = true
    this.engineTokenSignature = undefined
    if (!this.isVisible) {
      this.showPalette()
    } else if (this.palette) {
      this.palette.classList.remove('is-minimized')
      this.palette.classList.add('is-expanded')
    }
    this.clearOutsideHandler()
    this.engine.enter(command, opts)
  }

  isEngineActive(): boolean {
    return this.engineActive
  }

  isEngineCanvasMode(): boolean {
    return this.engineActive && this.engineCanvasMode
  }

  isEngineConnectionCanvasMode(): boolean {
    return this.engineActive && this.engineCanvasMode && Boolean(this.engine?.allowsConnectionHits())
  }

  isEnginePointCanvasMode(): boolean {
    return this.engineActive && this.engineCanvasMode && Boolean(this.engine?.isCanvasPointMode())
  }

  handleEngineCanvasSelect(value: string): void {
    if (!this.engine || !this.engineActive) return
    this.engine.handleCanvasSelect(value)
  }

  handleEngineCanvasPoint(point: { x: number, y: number }): void {
    if (!this.engine || !this.engineActive) return
    this.engine.handleCanvasPoint(point)
  }

  exitEngine(): void {
    if (!this.engine || !this.engineActive) return
    this.engine.exit()
  }

  refreshLiveInput(): void {
    if (this.engineActive) this.engine?.refreshLiveInput()
  }

  suppressNextPaletteToggle(): void {
    this.suppressNextToggle = true
  }

  private isLegacyCommandReady(command: Command): boolean {
    if (typeof command.ready === 'function') {
      try {
        return Boolean(command.ready())
      } catch {
        return false
      }
    }
    return command.ready !== false
  }

  private getCommandAutoClose(command: Command): boolean {
    if (this.engineActive) return false
    if (typeof command.autoClose === 'boolean') return command.autoClose
    return command.type === 'mode' ? false : true
  }

  private getCommandDescription(command: Command): string {
    return typeof command.description === 'function'
      ? command.description()
      : command.description
  }

  private getCommandGatedReason(command: Command): string | undefined {
    return typeof command.gatedReason === 'function'
      ? command.gatedReason()
      : command.gatedReason
  }

  private isCommandBaseVisible(command: Command): boolean {
    const modes = command.modes ?? ['edit']
    if (!modes.includes(this.currentMode)) return false
    const pillPolicy = selectPillCommandPolicy(command.name, {
      space: this.spaceKind,
      palette: 'go'
    })
    if (!pillPolicy.visible) return false
    if (typeof command.visible === 'function') {
      try {
        return Boolean(command.visible())
      } catch {
        return false
      }
    }
    return command.visible !== false
  }

  private resolveCommandAvailability(command: Command): CommandAvailability {
    const description = this.getCommandDescription(command)
    const gatedReason = this.getCommandGatedReason(command)
    if (!this.isCommandBaseVisible(command)) {
      return { state: 'hidden', description, gatedReason }
    }
    const resolved = typeof command.availability === 'function'
      ? command.availability()
      : command.availability
    if (resolved) {
      if (typeof resolved === 'string') {
        return { state: resolved, description, gatedReason }
      }
      return {
        state: resolved.state,
        description: resolved.description ?? description,
        gatedReason: resolved.gatedReason ?? gatedReason
      }
    }
    return {
      state: this.isLegacyCommandReady(command) ? 'armed' : 'disabled',
      description,
      gatedReason
    }
  }

  private resolveSlotOptionAvailability(option: SlotOption): PickerOptionAvailability {
    return {
      state: option.state ?? (option.disabled ? 'disabled' : 'armed'),
      description: option.description,
      gatedReason: option.gatedReason
    }
  }

  private isCommandSelectable(command: Command): boolean {
    const availability = this.resolveCommandAvailability(command)
    return availability.state === 'armed' || availability.state === 'gated'
  }

  private isCommandArmed(command: Command): boolean {
    return this.resolveCommandAvailability(command).state === 'armed'
  }

  private isSlotOptionSelectable(option: SlotOption): boolean {
    const availability = this.resolveSlotOptionAvailability(option)
    return availability.state === 'armed' || availability.state === 'gated'
  }

  private isSlotOptionArmed(option: SlotOption): boolean {
    return this.resolveSlotOptionAvailability(option).state === 'armed'
  }

  private createPalette(): HTMLDivElement {
    const palette = document.createElement('div')
    palette.className = 'cmd-palette is-memory-anonymous is-account-none'
    palette.classList.toggle('is-index-context', isDetachedSpace(this.spaceKind))
    if (this.isTouchDevice) {
      palette.classList.add('is-touch-device')
    }

    const preventZoom = (e: Event) => {
      const touchEvent = e as TouchEvent
      if (touchEvent.touches && touchEvent.touches.length > 1) {
        e.preventDefault()
      }
    }

    palette.addEventListener('touchstart', preventZoom, { passive: false })
    palette.addEventListener('gesturestart', (e) => e.preventDefault(), { passive: false } as any)
    palette.addEventListener('gesturechange', (e) => e.preventDefault(), { passive: false } as any)
    palette.addEventListener('gestureend', (e) => e.preventDefault(), { passive: false } as any)
    // Trackpad pinch arrives as ctrl+wheel outside Safari; swallow only that
    // so browser-UI zoom never fires over chrome while picker scroll stays native.
    palette.addEventListener('wheel', (e: WheelEvent) => {
      if (e.ctrlKey) e.preventDefault()
    }, { passive: false })

    palette.addEventListener('click', (e) => {
      if (this.suppressNextToggle) {
        this.suppressNextToggle = false
        return
      }
      const target = e.target as HTMLElement
      if (
        this.isVisible
        && (
          target.closest('.cmd-wheel')
          || target.closest('.cmd-index-list')
          || target.closest('.cmd-runner')
          || target.closest('.cmd-account')
          || target.closest('.cmd-top-bento-stack')
        )
      ) return
      if (!this.isVisible && isDetachedSpace(this.spaceKind)) return
      this.togglePalette()
    })

    const syncIndicator = document.createElement('div')
    syncIndicator.className = 'cmd-sync is-account-none'
    syncIndicator.setAttribute('aria-hidden', 'true')
    syncIndicator.addEventListener('click', (e) => {
      e.stopPropagation()
      e.preventDefault()
      this.syncClickHandler?.()
      if (!this.syncClickHandler) {
        this.openSyncSurface()
      }
    })
    const syncIcon = document.createElement('span')
    syncIcon.className = 'cmd-sync-icon'
    const syncGlyph = document.createElement('span')
    syncGlyph.className = 'cmd-sync-glyph'
    // User-facing glyph says account while the underlying surface still carries sync/account logic.
    syncGlyph.textContent = 'A'
    syncIcon.appendChild(syncGlyph)
    syncIndicator.appendChild(syncIcon)

    const indexIndicator = document.createElement('div')
    indexIndicator.className = 'cmd-index is-world-anonymous'
    indexIndicator.setAttribute('aria-hidden', 'true')
    indexIndicator.addEventListener('click', (e) => {
      e.stopPropagation()
      e.preventDefault()
      this.indexClickHandler?.()
      if (!this.indexClickHandler) {
        this.openIndexSurface()
      }
    })
    const indexIcon = document.createElement('span')
    indexIcon.className = 'cmd-index-icon'
    const indexGlyph = document.createElement('span')
    indexGlyph.className = 'cmd-index-glyph'
    indexGlyph.textContent = 'B'
    indexIcon.appendChild(indexGlyph)
    indexIndicator.appendChild(indexIcon)

    document.body.appendChild(palette)
    this.palette = palette
    this.syncIndicator = syncIndicator
    this.indexIndicator = indexIndicator
    this.renderCollapsedPaletteChrome(palette)
    return palette
  }

  private setupKeyboardShortcuts(): void {
    document.addEventListener('keydown', (e) => {
      if (!this.palette?.isConnected || e.defaultPrevented || e.isComposing) return
      const shortcutSurface = this.resolveShortcutSurface(e)
      if (shortcutSurface) {
        e.preventDefault()
        e.stopPropagation()
        this.handleSurfaceShortcut(shortcutSurface)
        return
      }

      if (this.shouldHandleAccountShortcut(e)) {
        e.preventDefault()
        e.stopPropagation()
        this.triggerAccountShortcut()
        return
      }
      if (!this.isVisible) return
      if (e.key === 'Escape') {
        if (this.engineActive) {
          e.preventDefault()
          this.exitEngine()
          return
        }
        this.hidePalette()
        return
      }
      if (this.engineRunnerState?.input && e.target instanceof HTMLInputElement) return
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        e.stopPropagation()
        if (this.surface === 'index' && this.indexPaletteState.rows?.length) {
          this.shiftIndexListSelection(-1)
          return
        }
        this.shiftSelection(-1)
        return
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        e.stopPropagation()
        if (this.surface === 'index' && this.indexPaletteState.rows?.length) {
          this.shiftIndexListSelection(1)
          return
        }
        this.shiftSelection(1)
        return
      }
      if (e.key === 'Enter') {
        if (this.surface === 'index' && this.indexPaletteState.rows?.length) {
          e.preventDefault()
          e.stopPropagation()
          this.hidePalette()
          return
        }
        if (this.engineActive && this.engineHasDone && this.engine) {
          e.preventDefault()
          e.stopPropagation()
          this.runEngineAction()
          return
        }
        if (this.wheelMode === 'slot' && this.slotPicker) {
          e.preventDefault()
          e.stopPropagation()
          this.executeSelectedCommand()
          // If a mode token is also active (like section), immediately trigger it after selection
          if (this.mode && this.modeTokenHandler) {
            const activeToken = this.mode.activeTokens?.[0]
            if (activeToken) {
              this.modeTokenHandler(activeToken)
            }
          }
          return
        }
        // In mode with a token handler and /done active, Enter triggers that handler.
        if (this.mode && this.modeTokenHandler && this.mode.activeTokens?.includes('done')) {
          e.preventDefault()
          e.stopPropagation()
          this.modeTokenHandler('done')
          return
        }
        e.preventDefault()
        e.stopPropagation()
        this.executeSelectedCommand()
      }
    })
  }

  private isTypingTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false
    if (target.isContentEditable) return true
    const tagName = target.tagName
    if (tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT') return true
    if (target.closest('[contenteditable="true"]')) return true
    return false
  }

  private getAccountLabelActions(label: string): string[] {
    const parsed = this.parseAccountStatusLabel(label)
    const actionsRaw = parsed?.actionsRaw ?? ''
    return actionsRaw
      .split('|')
      .map((part) => part.trim().toLowerCase())
      .filter(Boolean)
  }

  private getAccountActionViewModel(
    label: string,
    isActionEnabled?: (action: string) => boolean
  ): AccountActionViewModel | undefined {
    const actions = this.getAccountLabelActions(label)
    const action = actions.includes('logout')
      ? 'logout'
      : (actions.includes('login') ? 'login' : undefined)
    if (!action) return undefined
    return {
      action,
      enabled: isActionEnabled ? isActionEnabled(action) : true
    }
  }

  private getAccountNavigationLabel(label: string): string {
    const parsed = this.parseAccountStatusLabel(label)
    if (!parsed) return label
    const pipeIndex = label.indexOf(' | ')
    const structuralLabel = pipeIndex === -1 ? label : label.slice(0, pipeIndex)
    return `${structuralLabel} | account`
  }

  private handleAccountNavigationAction(action?: string): void {
    if (action?.trim().toLowerCase() !== 'account') return
    this.openSyncSurface()
  }

  private parseAccountStatusLabel(label: string): {
    key: string
    value: string
    actionsRaw: string
    hasAtPrefix: boolean
  } | null {
    const pipeIndex = label.indexOf(' | ')
    const structuralLabel = pipeIndex === -1 ? label : label.slice(0, pipeIndex)
    const actionsFromPipe = pipeIndex === -1 ? '' : label.slice(pipeIndex + 3).trim()
    const parts = structuralLabel.split(' : ').map(part => part.trim()).filter(Boolean)
    if (parts.length < 2) return null
    const key = parts[0]
    const value = parts[1]
    const actionsFromColon = parts.slice(2).join(' : ')
    const actionsRaw = actionsFromPipe.length ? actionsFromPipe : actionsFromColon
    return {
      key,
      value: value.startsWith('@') ? value.slice(1) : value,
      actionsRaw,
      hasAtPrefix: value.startsWith('@')
    }
  }

  private shouldHandleAccountShortcut(e: KeyboardEvent): boolean {
    if (!this.isVisible || this.surface !== 'sync' || this.engineActive || this.mode) return false
    if (this.isTypingTarget(e.target)) return false
    if (e.metaKey || e.ctrlKey || e.altKey) return false
    if (e.key.toLowerCase() !== 'l') return false
    if (document.querySelector('.auth-modal-backdrop.is-visible')) return false
    const action = this.getAccountActionViewModel(this.accountUserLabel, this.accountUserActionEnabled)
    return action?.enabled ?? false
  }

  private triggerAccountShortcut(): void {
    const action = this.getAccountActionViewModel(this.accountUserLabel, this.accountUserActionEnabled)
    if (!action?.enabled) return
    void this.accountUserHandler?.(action.action)
  }

  private resolveShortcutSurface(e: KeyboardEvent): 'go' | 'sync' | 'index' | undefined {
    if (e.defaultPrevented) return undefined
    if (this.isTypingTarget(e.target)) return undefined
    if (e.altKey && !e.metaKey && !e.ctrlKey) {
      if (e.key === '/') return 'go'
      if (e.key === '.') return 'sync'
      if (e.key === ',') return 'index'
      return undefined
    }
    if (this.isTouchDevice) return undefined
    if (e.metaKey || e.ctrlKey || e.altKey) return undefined
    const key = e.key.toLowerCase()
    if (key === 'g') return 'go'
    if (key === 'a') return 'sync'
    if (key === 'b') return 'index'
    return undefined
  }

  private handleSurfaceShortcut(surface: 'go' | 'sync' | 'index'): void {
    if (!this.isTouchDevice && !this.engineActive && !this.mode && this.isVisible && this.surface === surface) {
      this.hidePalette()
      return
    }
    if (surface === 'go') {
      this.openGoSurface()
      return
    }
    if (surface === 'sync') {
      if (this.syncClickHandler) {
        this.syncClickHandler()
        return
      }
      this.openSyncSurface()
      return
    }
    if (this.indexClickHandler) {
      this.indexClickHandler()
      return
    }
    this.openIndexSurface()
  }

  private openGoPalette(): void {
    if (this.isVisible) return
    this.applyPaletteTransition({
      overlay: 'expanded',
      affordance: 'go',
      denyGoInIndex: true,
      resetGoPicker: true
    })
  }

  private togglePalette(): void {
    if (this.isVisible) {
      if (this.engineActive) {
        this.exitEngine()
        return
      }
      if (this.mode) {
        if (this.modeExitHandler) this.modeExitHandler(this.mode.name)
      } else {
        this.applyPaletteTransition({ overlay: 'collapsed' })
      }
      return
    }
    this.openGoPalette()
  }

  private showPalette(): void {
    if (!this.palette) return

    this.isVisible = true
    this.palette.classList.add('is-expanded')
    this.palette.classList.toggle('is-touch', this.isTouchDevice)
    this.palette.classList.toggle('is-desktop', !this.isTouchDevice)
    this.palette.classList.toggle('is-minimized', Boolean(this.mode))
    this.palette.setAttribute('role', 'dialog')
    this.palette.setAttribute('aria-expanded', 'true')
    this.renderPaletteContents()

    if (!this.mode && !this.engineActive) {
      this.outsideHandler = (e: Event) => {
        const target = e.target as Node
        if (target instanceof Element) {
          if (target.closest('.auth-modal') || target.closest('.auth-modal-backdrop')) {
            return
          }
        }
        if (this.palette && !this.palette.contains(target)) {
          this.hidePalette('outside-tap')
        }
      }
      document.addEventListener('pointerdown', this.outsideHandler, true)
    }
  }

  private hidePalette(reason: PaletteHideReason = 'programmatic'): void {
    if (this.engineActive) {
      this.exitEngine()
      return
    }
    if (this.mode) {
      if (this.modeExitHandler) this.modeExitHandler(this.mode.name)
      return
    }
    if (!this.palette) return
    const wasVisible = this.isVisible
    this.isVisible = false
    this.setSurface('go')
    this.palette.classList.remove('is-expanded', 'is-touch', 'is-desktop', 'is-minimized', 'is-sync-surface', 'is-index-surface', 'is-update-surface')
    this.palette.classList.toggle('is-index-context', isDetachedSpace(this.spaceKind))
    this.renderCollapsedPaletteChrome(this.palette)
    this.slotPicker = undefined
    this.slotPickerTapValue = undefined
    this.wheelMode = 'commands'
    this.wheelEl = undefined
    this.wheelItems = []
    this.indexListEl = undefined
    this.indexListItems = []
    this.indexListSelectedIndex = -1
    this.promptButton = undefined
    this.promptLabelEl = undefined
    this.promptCommandEl = undefined
    this.promptStepEl = undefined
    this.promptFeedbackEl = undefined
    this.promptDescriptionBodyEl = undefined
    this.descriptionNameEl = undefined
    this.descriptionDashEl = undefined
    this.descriptionCopyEl = undefined
    this.rowHeight = 0
    if (this.wheelRaf !== null) {
      cancelAnimationFrame(this.wheelRaf)
      this.wheelRaf = null
    }
    if (this.indexListScrollReleaseTimer) {
      window.clearTimeout(this.indexListScrollReleaseTimer)
      this.indexListScrollReleaseTimer = undefined
    }
    if (this.outsideHandler) {
      document.removeEventListener('pointerdown', this.outsideHandler, true)
      this.outsideHandler = undefined
    }
    this.scheduleCollapsedPaletteIntegrityCheck(this.palette)
    if (wasVisible && this.onHideCallback) {
      this.onHideCallback(reason)
    }
  }

  private scheduleCollapsedPaletteIntegrityCheck(palette: HTMLDivElement): void {
    const verify = () => {
      if (this.palette !== palette || this.isVisible || palette.classList.contains('is-expanded')) return
      if (palette.querySelector('.cmd-pill-shell')) return
      this.renderCollapsedPaletteChrome(palette)
    }
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(verify)
      return
    }
    verify()
  }

  executeCommand(name: string): void {
    const command = this.commands.get(name)
    if (!command) return
    this.lastUsedCommandName = name
    const result = command.execute()
    const autoClose = this.getCommandAutoClose(command)
    if (autoClose) {
      if (result instanceof Promise) {
        result.finally(() => this.hidePalette()).catch(() => {})
      } else {
        this.hidePalette()
      }
    }
  }

  private resortCommands(): void {
    const core = sortCommandListItems(
      Array.from(this.commands.values())
        .filter(command => this.resolveCommandAvailability(command).state !== 'hidden'),
      command => command.name
    )
    this.sortedCommands = [this.pickCommand, ...core]
  }

  private buildWheel(target?: HTMLElement): void {
    if (!this.palette) return
    const host = target ?? this.palette
    const existingWheel = host.querySelector('.cmd-wheel')
    if (existingWheel) existingWheel.remove()
    const usingSlot = this.wheelMode === 'slot' && Boolean(this.slotPicker)
    const slot = this.slotPicker
    const wheel = document.createElement('div')
    wheel.className = 'cmd-wheel'
    if (this.surface === 'index') {
      wheel.classList.add('cmd-index-picker')
    }
    wheel.setAttribute('role', 'listbox')
    wheel.tabIndex = -1

    const list = document.createElement('div')
    list.className = 'cmd-wheel-list'
    this.wheelItems = []
    const appendSeparator = () => {
      const last = list.lastElementChild
      if (last?.classList.contains('cmd-wheel-separator')) return
      list.appendChild(this.createWheelSeparator())
    }

    const items = usingSlot ? slot?.options ?? [] : this.sortedCommands
    let previousSlotGroup: string | undefined
    let inEditGroup = false
    let inMoveTextGroup = false
    let inSelectGroup = false
    let insertedSGroupStart = false
    let insertedVGroupStart = false
    let insertedHeaderGap = false
    let previousDomainGroup: 'account' | 'settings' | 'space' | 'user' | undefined
    items.forEach((entry, index) => {
      if (usingSlot) {
          const option = entry as SlotOption
          if (option.value === 'pick/') {
            if (!insertedHeaderGap) {
            appendSeparator()
            insertedHeaderGap = true
          }
          previousSlotGroup = undefined
        } else {
          const slotGroup = option.group ?? (option.value.split('-')[0] ?? option.value)
          if ((this.surface === 'sync' || this.surface === 'index') && previousSlotGroup && previousSlotGroup !== slotGroup) {
            appendSeparator()
          }
          previousSlotGroup = slotGroup
        }
      }
        if (!usingSlot) {
          const command = entry as Command
          if (command.name === this.pickCommand.name && !insertedHeaderGap) {
            appendSeparator()
            insertedHeaderGap = true
          }
          const lowerName = command.name.toLowerCase()
          const domainGroup =
            lowerName.startsWith('account-')
              ? 'account'
              : lowerName.startsWith('settings-')
                ? 'settings'
              : lowerName.startsWith('space-')
              ? 'space'
              : lowerName.startsWith('user-')
                ? 'user'
                : undefined
          if (domainGroup && previousDomainGroup && previousDomainGroup !== domainGroup) {
            appendSeparator()
          }
          if (domainGroup) {
            previousDomainGroup = domainGroup
          }
        const isSGroup = lowerName.startsWith('s')
        const isVGroup = lowerName.startsWith('v')
        const isSelect = lowerName.startsWith('select')
        const isEdit = command.name.startsWith('edit-')
        const isMoveText = lowerName.startsWith('move-text-')
        if (!insertedSGroupStart && isSGroup) {
          appendSeparator()
          insertedSGroupStart = true
        }
        if (!insertedVGroupStart && isVGroup) {
          appendSeparator()
          insertedVGroupStart = true
        }
        // Reusable boundary separators for grouped command blocks.
        if (!inEditGroup && isEdit) {
          appendSeparator()
          inEditGroup = true
        } else if (inEditGroup && !isEdit) {
          appendSeparator()
          inEditGroup = false
        }
        if (!inMoveTextGroup && isMoveText) {
          appendSeparator()
          inMoveTextGroup = true
        } else if (inMoveTextGroup && !isMoveText) {
          appendSeparator()
          inMoveTextGroup = false
        }
        if (!inSelectGroup && isSelect) {
          inSelectGroup = true
        } else if (inSelectGroup && !isSelect) {
          appendSeparator()
          inSelectGroup = false
        }
      }

      const item = document.createElement('div')
      item.className = 'cmd-wheel-item'
      item.dataset.index = String(index)
      item.setAttribute('role', 'option')
        if (usingSlot) {
          const option = entry as SlotOption
          const availability = this.resolveSlotOptionAvailability(option)
          item.textContent = option.label
          if (option.value.startsWith('pick/')) {
            item.classList.add('is-placeholder')
          }
          const disabled = availability.state === 'disabled'
          item.classList.toggle('is-disabled', disabled)
          item.classList.toggle('is-gated', availability.state === 'gated')
        item.setAttribute('aria-disabled', disabled ? 'true' : 'false')
          item.addEventListener('click', () => {
          if (disabled) return
          if (index !== this.selectedIndex) {
            this.setSelectedIndex(index, { scroll: false })
            if (this.slotPicker?.previewOnHighlight) {
              const result = this.slotPicker.onSelect(option.value)
              if (result instanceof Promise) {
                void result.catch(() => {})
              }
            }
            this.slotPickerTapValue = this.slotPicker?.previewOnHighlight ? option.value : undefined
            this.focusPrompt()
            return
          }
          if (availability.state !== 'armed') {
            this.focusPrompt()
            return
          }
          if (this.slotPicker?.previewOnHighlight && this.slotPickerTapValue !== option.value) {
            const result = this.slotPicker?.onSelect(option.value)
            if (result instanceof Promise) {
              void result.catch(() => {})
            }
            this.slotPickerTapValue = option.value
            this.focusPrompt()
            return
          }
          this.focusPrompt()
          this.pressThen(item, () => this.executeSelectedCommand())
        })
      } else {
        const command = entry as Command
        const availability = this.resolveCommandAvailability(command)
        item.textContent = command.name
        const disabled = availability.state === 'disabled'
        if (command.name === this.pickCommand.name) {
          item.classList.add('is-placeholder')
          item.textContent = this.getPickerPlaceholderText()
        }
        item.classList.toggle('is-disabled', disabled)
        item.classList.toggle('is-gated', availability.state === 'gated')
        item.setAttribute('aria-disabled', disabled ? 'true' : 'false')
        item.addEventListener('click', () => {
          if (disabled) return
          if (index === this.selectedIndex && availability.state === 'armed') {
            this.pressThen(item, () => this.executeSelectedCommand())
            return
          }
          this.setSelectedIndex(index, { scroll: false })
          this.focusPrompt()
        })
      }
      list.appendChild(item)
      if (item.classList.contains('is-placeholder')) {
        appendSeparator()
      }
      this.wheelItems.push(item)
    })

    // Empty state fallback
    if (!this.wheelItems.length) {
      const empty = document.createElement('div')
      empty.className = 'cmd-wheel-empty'
      empty.textContent = usingSlot ? 'No options available' : 'No commands available'
      list.appendChild(empty)
    }

    wheel.appendChild(list)
    wheel.addEventListener('scroll', () => this.handleWheelScroll())
    host.appendChild(wheel)
    this.wheelEl = wheel
  }

  private getPickerPlaceholderText(): string {
    if (this.surface === 'sync') {
      if (this.syncBentoState.syncPickerPlaceholder?.trim()) {
        return this.syncBentoState.syncPickerPlaceholder
      }
      return 'account command list:'
    }
    return 'command list A-Z:'
  }

  private createWheelSeparator(): HTMLDivElement {
    const separator = document.createElement('div')
    separator.className = 'cmd-wheel-separator'
    separator.setAttribute('role', 'presentation')
    separator.setAttribute('aria-hidden', 'true')
    return separator
  }

  private createInlineSeparatorToken(text: string): HTMLSpanElement {
    const separator = document.createElement('span')
    separator.className = 'cmd-runner-separator'
    separator.textContent = text
    return separator
  }

  private createRunnerShell(input: {
    panelClassName: string
    promptClassName: string
    promptDisabled?: boolean
    feedbackVisible?: boolean
    onPromptClick: () => void
  }): RunnerShellRefs {
    if (!this.palette) {
      throw new Error('palette unavailable')
    }
    const panel = document.createElement('div')
    panel.className = input.panelClassName

    const header = document.createElement('div')
    header.className = 'cmd-runner-header cmd-runner-screen'

    const promptButton = document.createElement('button')
    promptButton.className = input.promptClassName
    promptButton.type = 'button'
    promptButton.disabled = input.promptDisabled === true
    promptButton.addEventListener('click', () => {
      input.onPromptClick()
    })

    const tokensEl = document.createElement('span')
    tokensEl.className = 'cmd-runner-tokens'
    promptButton.appendChild(tokensEl)
    header.appendChild(promptButton)

    const feedbackLine = document.createElement('div')
    feedbackLine.className = 'cmd-runner-feedback-line'
    feedbackLine.style.display = input.feedbackVisible === false ? 'none' : ''
    const feedbackEl = document.createElement('span')
    feedbackEl.className = 'cmd-runner-feedback'
    feedbackLine.appendChild(feedbackEl)

    const descriptionBodyEl = document.createElement('div')
    descriptionBodyEl.className = 'cmd-runner-body'
    const descriptionCopyWrap = document.createElement('div')
    descriptionCopyWrap.className = 'cmd-runner-copy-wrap'
    const descriptionCopyEl = document.createElement('span')
    descriptionCopyEl.className = 'cmd-runner-copy'
    descriptionCopyWrap.appendChild(descriptionCopyEl)
    descriptionBodyEl.appendChild(descriptionCopyWrap)

    panel.appendChild(header)
    panel.appendChild(feedbackLine)
    panel.appendChild(descriptionBodyEl)
    this.palette.appendChild(panel)
    this.palette.appendChild(this.buildCloseButtonBento())

    return {
      promptButton,
      tokensEl,
      feedbackEl,
      descriptionBodyEl,
      descriptionCopyEl
    }
  }

  private appendRunnerToken(
    parent: HTMLElement,
    input: {
      className: string
      text: string
    }
  ): HTMLSpanElement {
    const token = document.createElement('span')
    token.className = input.className
    token.textContent = input.text
    parent.appendChild(token)
    return token
  }

  private buildPrompt(): void {
    if (!this.palette) return
    const existingRunner = this.palette.querySelector('.cmd-runner.cmd-runner-main')
    if (existingRunner) existingRunner.remove()
    const shell = this.createRunnerShell({
      panelClassName: 'cmd-runner cmd-runner-main',
      promptClassName: 'cmd-runner-prompt',
      promptDisabled: true,
      feedbackVisible: false,
      onPromptClick: () => {
        if (this.engineActive && this.engineRunnerState?.input) {
          return
        }
        if (this.engineActive && this.engineHasDone && this.engine) {
          this.engine.handleDone()
          return
        }
        if (this.mode && this.modeTokenHandler && this.mode.activeTokens?.includes('done')) {
          this.modeTokenHandler('done')
          return
        }
        this.executeSelectedCommand()
      }
    })
    const label = this.appendRunnerToken(shell.tokensEl, {
      className: 'cmd-runner-label',
      text: this.getPromptPrefix()
    })
    shell.tokensEl.appendChild(this.createInlineSeparatorToken('/'))
    const commandName = this.appendRunnerToken(shell.tokensEl, {
      className: 'cmd-runner-command',
      text: ''
    })
    const commandStep = this.appendRunnerToken(shell.tokensEl, {
      className: 'cmd-runner-step',
      text: ''
    })

    this.promptButton = shell.promptButton
    this.promptLabelEl = label
    this.promptCommandEl = commandName
    this.promptStepEl = commandStep
    this.promptFeedbackEl = shell.feedbackEl
    this.promptDescriptionBodyEl = shell.descriptionBodyEl
    this.descriptionCopyEl = shell.descriptionCopyEl
  }

  private applyInitialSelection(): void {
    const length = this.getActiveListLength()
    if (!length) {
      this.disarmPrompt()
      return
    }
    if (this.wheelMode === 'slot' && this.slotPicker) {
      const start = this.findFirstEnabledIndex()
      this.setSelectedIndex(start === -1 ? 0 : start, { scroll: true, immediate: true })
      return
    }
    const preferred = this.lastUsedCommandName ?? 'pick/'
    let startIndex = this.sortedCommands.findIndex(cmd => cmd.name === preferred)
    if (startIndex === -1) startIndex = this.findFirstEnabledIndex()
    this.setSelectedIndex(startIndex === -1 ? 0 : startIndex, { scroll: true, immediate: true })
    this.focusPrompt()
  }

  private measureRowHeight(): void {
    if (this.rowHeight || !this.wheelItems.length) return
    this.rowHeight = this.wheelItems[0].getBoundingClientRect().height
  }

  private getActiveListLength(): number {
    if (this.wheelMode === 'slot' && this.slotPicker) {
      return this.slotPicker.options.length
    }
    return this.sortedCommands.length
  }

  private isIndexEnabled(index: number): boolean {
    if (this.wheelMode === 'slot' && this.slotPicker) {
      const option = this.slotPicker.options[index]
      return Boolean(option && this.isSlotOptionSelectable(option))
    }
    const command = this.sortedCommands[index]
    return Boolean(command && this.isCommandSelectable(command))
  }

  private findFirstEnabledIndex(): number {
    const length = this.getActiveListLength()
    for (let i = 0; i < length; i++) {
      if (this.isIndexEnabled(i)) return i
    }
    return -1
  }

  private findNextEnabledIndex(start: number, delta: number): number {
    const length = this.getActiveListLength()
    if (!length) return -1
    let idx = start
    for (let i = 0; i < length; i++) {
      idx = (idx + delta + length) % length
      if (this.isIndexEnabled(idx)) return idx
    }
    return -1
  }

  private setSelectedIndex(
    index: number,
    options: { scroll?: boolean, immediate?: boolean, direction?: number } = {}
  ): void {
    const length = this.getActiveListLength()
    if (!length) {
      this.selectedIndex = -1
      this.disarmPrompt()
      return
    }
    const clamped = Math.min(Math.max(index, 0), length - 1)
    let nextIndex = clamped
    if (!this.isIndexEnabled(nextIndex)) {
      const direction = options.direction ?? 1
      const candidate = this.findNextEnabledIndex(nextIndex, direction)
      nextIndex = candidate === -1 ? nextIndex : candidate
    }
    this.selectedIndex = nextIndex
    this.measureRowHeight()
    if (options.scroll) {
      this.scrollWheelToIndex(nextIndex, options.immediate)
    }
    this.updateSelectionUI()
  }

  private getCurrentSelection(): { kind: 'command', command: Command } | { kind: 'slot', option: SlotOption } | undefined {
    if (this.wheelMode === 'slot' && this.slotPicker) {
      const option = this.slotPicker.options[this.selectedIndex]
      if (!option) return undefined
      return { kind: 'slot', option }
    }
    const command = this.sortedCommands[this.selectedIndex]
    if (!command) return undefined
    return { kind: 'command', command }
  }

  private updateSelectionUI(): void {
    const selection = this.getCurrentSelection()
    this.wheelItems.forEach((item, idx) => {
      item.classList.toggle('is-selected', idx === this.selectedIndex)
      item.setAttribute('aria-selected', idx === this.selectedIndex ? 'true' : 'false')
    })

    if (this.engineActive) {
      if (this.slotPicker && this.selectedIndex !== this.lastHighlightedSlotIndex) {
        const option = this.slotPicker.options[this.selectedIndex]
        if (option && this.slotPicker.onHighlight) {
          this.slotPicker.onHighlight(option.value)
        }
        this.lastHighlightedSlotIndex = this.selectedIndex
      }
      return
    }

    // When a mode is active and the slot picker is open, keep the mode runner intact.
    if (this.mode && this.wheelMode === 'slot') {
      // Propagate highlight to slot picker if needed
      if (this.slotPicker && this.selectedIndex !== this.lastHighlightedSlotIndex) {
        const option = this.slotPicker.options[this.selectedIndex]
        if (option && this.slotPicker.onHighlight) {
          this.slotPicker.onHighlight(option.value)
        }
        this.lastHighlightedSlotIndex = this.selectedIndex
      }
      this.applyModeStatus()
      return
    }

    if (!selection) {
      this.disarmPrompt()
      return
    }

    if (selection.kind === 'command') {
      const command = selection.command
      if (command.name === this.pickCommand.name) {
        this.enterPickState()
      } else {
        this.applyDescription(command)
        this.armPrompt(command)
      }
      return
    }

    this.applySlotDescription(selection.option)
    if (selection.option.value === 'pick/') {
      return
    }
    this.armPrompt(undefined, selection.option)
  }

  private scrollWheelToIndex(index: number, immediate?: boolean): void {
    if (!this.wheelEl) return
    this.measureRowHeight()
    if (!this.rowHeight) return
    const target = index * this.rowHeight
    this.suppressScrollSync = true
    if (this.scrollSyncReleaseTimer) {
      window.clearTimeout(this.scrollSyncReleaseTimer)
      this.scrollSyncReleaseTimer = undefined
    }
    if (immediate) {
      this.wheelEl.scrollTop = target
      this.suppressScrollSync = false
      return
    }
    this.wheelEl.scrollTo({ top: target, behavior: 'smooth' })
    this.scrollSyncReleaseTimer = window.setTimeout(() => {
      this.suppressScrollSync = false
      this.scrollSyncReleaseTimer = undefined
    }, 200)
  }

  private applyDescription(command?: Command): void {
    if (!command) {
      if (this.descriptionNameEl) this.descriptionNameEl.textContent = ''
      if (this.descriptionCopyEl) this.descriptionCopyEl.textContent = ''
      if (this.descriptionDashEl) this.descriptionDashEl.style.display = 'none'
      this.setFeedback()
      return
    }
    this.setPromptLabel(command.name)
    const availability = this.resolveCommandAvailability(command)
    const description = availability.state === 'gated'
      ? (availability.gatedReason ?? availability.description ?? '')
      : (availability.description ?? '')
    if (this.descriptionNameEl) this.descriptionNameEl.textContent = command.name
    if (this.descriptionCopyEl) this.descriptionCopyEl.textContent = description
    if (this.descriptionDashEl) this.descriptionDashEl.style.display = description ? 'inline' : 'none'
    this.setFeedback()
  }

  private applySlotDescription(option: SlotOption): void {
    if (option.value === 'pick/') {
      this.enterPickState()
      return
    }
    const availability = this.resolveSlotOptionAvailability(option)
    const description = availability.state === 'gated'
      ? (availability.gatedReason ?? availability.description ?? option.label)
      : (availability.description ?? option.label)
    if (this.descriptionCopyEl) this.descriptionCopyEl.textContent = description
    if (this.descriptionNameEl) this.descriptionNameEl.textContent = ''
    if (this.descriptionDashEl) this.descriptionDashEl.style.display = 'none'
    if (this.promptLabelEl) this.promptLabelEl.textContent = this.getPromptPrefix()
    if (this.promptCommandEl) this.promptCommandEl.textContent = option.label
    if (this.promptStepEl) this.promptStepEl.textContent = ''
    this.setFeedback()
  }

  private applyModeStatus(): void {
    if (!this.mode) return
    if (this.palette) {
      this.palette.classList.add('is-expanded')
      if (this.slotPicker) {
        this.palette.classList.remove('is-minimized')
      } else {
        this.palette.classList.add('is-minimized')
      }
    }
    if (this.promptButton) {
      this.promptButton.classList.remove('is-engine')
      const hasDone = Boolean(this.mode.activeTokens?.includes('done'))
      this.promptButton.disabled = !hasDone
      this.promptButton.classList.toggle('is-armed', hasDone)
      if (hasDone) {
        this.promptButton.setAttribute('aria-label', 'done')
      } else {
        this.promptButton.removeAttribute('aria-label')
      }
    }
    if (this.promptLabelEl) this.promptLabelEl.textContent = this.getPromptPrefix()
    const label = this.mode.label ?? this.mode.name
    this.setPromptLabel(label, this.mode.activeTokens)
    if (this.descriptionNameEl) this.descriptionNameEl.textContent = ''
    if (this.descriptionDashEl) this.descriptionDashEl.style.display = 'none'
    if (this.descriptionCopyEl) this.descriptionCopyEl.textContent = this.mode.message
  }

  private armPrompt(command?: Command, slotOption?: SlotOption): void {
    if (!this.promptButton || !this.promptLabelEl || !this.promptCommandEl) return
    const isSlot = Boolean(slotOption)
    const label = isSlot ? slotOption?.label ?? '' : command?.name ?? ''
    const isArmed = command
      ? this.isCommandArmed(command)
      : (slotOption ? this.isSlotOptionArmed(slotOption) : false)
    this.promptButton.disabled = false
    this.promptButton.classList.add('is-armed')
    this.promptButton.classList.remove('is-engine')
    this.promptLabelEl.textContent = this.getPromptPrefix()
    this.setPromptLabel(label)
    this.promptButton.setAttribute('aria-label', `Execute ${label}`)
    if (!isArmed) {
      this.promptButton.disabled = true
      this.promptButton.classList.remove('is-armed')
      this.promptButton.removeAttribute('aria-label')
      this.promptCommandEl.classList.remove('cmd-runner-token-active')
    }
  }

  private disarmPrompt(): void {
    if (!this.promptButton || !this.promptLabelEl || !this.promptCommandEl) return
    this.promptButton.disabled = true
    this.promptButton.classList.remove('is-armed')
    this.promptButton.classList.remove('is-engine')
    this.promptLabelEl.textContent = this.getPromptPrefix()
    this.setPromptLabel('')
    this.promptButton.removeAttribute('aria-label')
    this.applyDescription(undefined)
  }

  private enterPickState(): void {
    if (!this.promptButton || !this.promptLabelEl || !this.promptCommandEl) return
    this.promptButton.disabled = true
    this.promptButton.classList.remove('is-armed')
    this.promptLabelEl.textContent = this.getPromptPrefix()
    this.setPromptLabel('')
    this.promptButton.removeAttribute('aria-label')
    if (this.descriptionCopyEl) {
      this.renderPickDescription(this.descriptionCopyEl)
    }
    if (this.descriptionNameEl) this.descriptionNameEl.textContent = ''
    if (this.descriptionDashEl) this.descriptionDashEl.style.display = 'none'
  }

  private renderPickDescription(target: HTMLElement): void {
    target.textContent = this.getCommandDescription(this.pickCommand)

    target.appendChild(document.createElement('br'))
    STATIC_LEGAL_LINKS.forEach((link, index) => {
      if (index > 0) {
        target.appendChild(document.createTextNode(' | '))
      }
      const anchor = document.createElement('a')
      anchor.href = link.href
      anchor.target = '_blank'
      anchor.rel = 'noopener noreferrer'
      anchor.textContent = link.label
      anchor.addEventListener('click', (e) => {
        e.stopPropagation()
      })
      target.appendChild(anchor)
    })
  }

  private handleWheelScroll(): void {
    if (!this.wheelEl || !this.getActiveListLength()) return
    if (this.suppressScrollSync) return
    if (this.wheelRaf !== null) return
    this.wheelRaf = requestAnimationFrame(() => {
      this.wheelRaf = null
      this.measureRowHeight()
    })
  }

  private shiftSelection(delta: number): void {
    const length = this.getActiveListLength()
    if (!length) return
    const nextIndex = this.findNextEnabledIndex(this.selectedIndex, delta)
    const target = nextIndex === -1 ? this.selectedIndex + delta : nextIndex
    this.setSelectedIndex(target, { scroll: true, direction: delta })
  }

  private async executeSelectedCommand(): Promise<void> {
    if (this.wheelMode === 'slot' && this.slotPicker) {
      const option = this.slotPicker.options[this.selectedIndex]
      if (!option || !this.isSlotOptionArmed(option)) return
      if (option.value === 'pick/') return
      const previewResult = this.slotPicker.onSelect(option.value)
      if (previewResult instanceof Promise) {
        await previewResult.catch(() => {})
      }
      const result = this.slotPicker.onConfirm
        ? this.slotPicker.onConfirm()
        : undefined
      if (result instanceof Promise) {
        await result.catch(() => {})
      }
      const shouldClose = this.slotPicker?.closeOnSelect !== false
      if (shouldClose) {
        this.closeSlotPicker({ minimize: true })
      }
      return
    }
    const command = this.sortedCommands[this.selectedIndex]
    if (!command || command.name === this.pickCommand.name) return
    if (!this.isCommandArmed(command)) return
    this.executeCommand(command.name)
  }

  private focusPrompt(): void {
    if (!this.promptButton) return
    this.promptButton.focus({ preventScroll: true })
  }

  private showRunnerResult(message: string, tone: 'success' | 'error'): void {
    this.setFeedback(message, tone)
  }

  private resolveLayoutState(): PaletteLayoutState {
    return {
      space: this.spaceKind,
      palette: this.surface,
      overlay: this.isVisible ? 'expanded' : 'collapsed'
    }
  }

  private resolveBentoViewModel(layout: PaletteLayoutState): PaletteBentoViewModel {
    if (layout.overlay === 'collapsed') {
      return { top: 'none', middle: 'none', bottom: 'none' }
    }
    if (layout.palette === 'sync') {
      return { top: 'sync-bento', middle: 'wheel', bottom: 'runner' }
    }
    if (layout.palette === 'index') {
      return { top: 'none', middle: 'index-stack', bottom: 'runner' }
    }
    if (layout.palette === 'update') {
      return { top: 'none', middle: 'none', bottom: 'notice' }
    }
    return {
      top: this.engineActive ? 'none' : 'account',
      middle: (!this.engineActive || Boolean(this.slotPicker)) ? 'wheel' : 'none',
      bottom: 'runner'
    }
  }

  private renderPaletteContents(): void {
    if (!this.palette) return
    const layout = this.resolveLayoutState()
    const viewModel = this.resolveBentoViewModel(layout)
    if (this.engineActive && this.engineRunnerState?.input && this.promptStepEl?.isConnected
      && viewModel.top === 'none' && viewModel.middle === 'none' && viewModel.bottom === 'runner') {
      this.renderEngineRunnerState(this.engineRunnerState)
      return
    }
    this.palette.innerHTML = ''
    this.wheelEl = undefined
    this.wheelItems = []
    this.promptButton = undefined
    this.promptLabelEl = undefined
    this.promptCommandEl = undefined
    this.promptStepEl = undefined
    this.promptFeedbackEl = undefined
    this.promptDescriptionBodyEl = undefined
    this.descriptionNameEl = undefined
    this.descriptionDashEl = undefined
    this.descriptionCopyEl = undefined
    this.rowHeight = 0
    if (this.wheelRaf !== null) {
      cancelAnimationFrame(this.wheelRaf)
      this.wheelRaf = null
    }
    this.wheelMode = this.slotPicker ? 'slot' : 'commands'
    if (!this.slotPicker) {
      this.resortCommands()
    }
    if (viewModel.top === 'sync-bento') {
      this.palette.classList.remove('is-minimized')
      this.palette.classList.add('is-sync-surface')
      this.palette.classList.remove('is-index-surface')
      this.palette.classList.remove('is-update-surface')
      this.configureSyncSlotPicker()
      this.buildSyncInfoBento()
      if (viewModel.middle === 'wheel') this.buildWheel()
      if (viewModel.bottom === 'runner') this.buildPrompt()
      this.applyInitialSelection()
      return
    }
    this.palette.classList.remove('is-sync-surface')
    if (viewModel.middle === 'index-stack') {
      this.palette.classList.remove('is-minimized')
      this.palette.classList.add('is-index-surface')
      this.palette.classList.remove('is-update-surface')
      this.buildIndexMiddleStack()
      if (viewModel.bottom === 'runner') this.buildPrompt()
      this.applyInitialIndexSelection()
      this.applyInitialSelection()
      return
    }
    this.palette.classList.remove('is-index-surface')
    if (viewModel.bottom === 'notice') {
      this.palette.classList.remove('is-minimized')
      this.palette.classList.add('is-update-surface')
      this.buildNoticeRunner()
      return
    }
    this.palette.classList.remove('is-update-surface')
    if (viewModel.top === 'account') {
      this.buildAccountBar()
    } else {
      const existingAccount = this.palette.querySelector('.cmd-account')
      if (existingAccount) existingAccount.remove()
      this.accountBar = undefined
    }
    if (viewModel.middle === 'wheel') {
      this.buildWheel()
    }
    if (viewModel.bottom === 'runner') this.buildPrompt()
    if (this.engineActive && this.engineRunnerState) {
      this.renderEngineRunnerState(this.engineRunnerState)
      return
    }
    if (this.mode) {
      this.applyModeStatus()
    } else {
      if (this.palette) {
        this.palette.classList.remove('is-minimized')
      }
      this.applyInitialSelection()
    }
  }

  private buildSyncInfoBento(): void {
    if (!this.palette) return
    const topStack = document.createElement('div')
    topStack.className = 'cmd-top-bento-stack'

    const panel = document.createElement('div')
    panel.className = 'cmd-account cmd-sync-info-bento'

    const context = document.createElement('div')
    context.className = 'cmd-sync-context'
    const userTone = this.syncBentoState.userTone ?? 'neutral'
    const user = document.createElement('div')
    user.className = 'cmd-sync-context-row cmd-account-user is-static'
    this.fillAccountStatusLabel(user, this.syncBentoState.userLabel, userTone, {
      onActionClick: this.syncBentoState.onUserAction,
      isActionEnabled: this.syncBentoState.isUserActionEnabled,
      renderActions: false
    })
    const sync = document.createElement('div')
    sync.className = 'cmd-sync-context-row cmd-sync-context-sync'
    this.fillSyncStateLabel(sync, this.syncBentoState.syncLabel)
    const network = document.createElement('div')
    network.className = 'cmd-sync-context-row cmd-sync-context-network'
    this.fillSyncStateLabel(network, this.syncBentoState.networkLabel)
    const syncDetail = document.createElement('div')
    syncDetail.className = 'cmd-sync-context-row cmd-sync-context-detail'
    this.fillSyncDetailLabel(syncDetail, this.syncBentoState.syncDetail)
    context.appendChild(user)
    context.appendChild(sync)
    if (this.syncBentoState.syncDetail) {
      context.appendChild(syncDetail)
    }
    if (this.syncBentoState.networkLabel) {
      context.appendChild(network)
    }

    panel.appendChild(context)
    const accountAction = this.getAccountActionViewModel(
      this.syncBentoState.userLabel,
      this.syncBentoState.isUserActionEnabled
    )
    if (accountAction) {
      panel.appendChild(this.buildAccountActionButton(accountAction, this.syncBentoState.onUserAction))
    }
    topStack.appendChild(panel)
    this.palette.appendChild(topStack)
  }

  private buildAccountActionButton(
    action: AccountActionViewModel,
    onAction?: (action?: string) => void | Promise<void>
  ): HTMLButtonElement {
    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'cmd-account-action-button'
    button.textContent = action.action
    button.disabled = !action.enabled
    if (!action.enabled) {
      button.classList.add('is-disabled')
    }
    button.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
      if (!action.enabled) return
      void onAction?.(action.action)
    })
    return button
  }

  private configureSyncSlotPicker(): void {
    const cloudActions = sortCommandListValues(this.syncBentoState.cloudActions ?? [])
    this.slotPicker = {
      label: 'pick/',
      options: [
        { label: this.getPickerPlaceholderText(), value: 'pick/' },
        ...cloudActions.map(action => {
          const availability = this.syncBentoState.resolveCloudActionAvailability?.(action)
            ?? { state: 'armed' as const }
          return {
            label: action,
            value: action,
            description: availability.description,
            gatedReason: availability.gatedReason,
            state: availability.state
          }
        })
      ],
      onSelect: (value) => this.syncBentoState.onCloudAction?.(value),
      closeOnSelect: false
    }
    this.wheelMode = 'slot'
  }

  private buildNoticeRunner(): void {
    if (!this.palette || !this.noticeState) return
    const panel = document.createElement('div')
    panel.className = 'cmd-runner cmd-notice'

    const feedbackLine = document.createElement('div')
    feedbackLine.className = 'cmd-runner-feedback-line'
    const feedbackEl = document.createElement('span')
    feedbackEl.className = 'cmd-runner-feedback is-success'
    feedbackEl.textContent = `> ${this.noticeState.feedback}`
    feedbackLine.appendChild(feedbackEl)

    // Each choice is its own real button so press feedback stays scoped to
    // the row under the finger, never the whole notice.
    const choicesEl = document.createElement('div')
    choicesEl.className = 'cmd-notice-choices cmd-runner-screen'
    this.noticeState.choices.forEach(choice => {
      const button = document.createElement('button')
      button.type = 'button'
      button.className = `cmd-notice-choice is-${choice.tone}`
      button.textContent = choice.label
      button.addEventListener('click', () => {
        void choice.onPick()
      })
      choicesEl.appendChild(button)
    })

    const body = document.createElement('div')
    body.className = 'cmd-runner-body'
    const copyWrap = document.createElement('div')
    copyWrap.className = 'cmd-runner-copy-wrap'
    const copyEl = document.createElement('span')
    copyEl.className = 'cmd-runner-copy'
    copyEl.textContent = this.noticeState.copy
    copyWrap.appendChild(copyEl)
    body.appendChild(copyWrap)

    // House runner order: interactive rows on the screen wash, then the
    // > feedback and plain-language copy below in the shared voice slot.
    panel.appendChild(choicesEl)
    panel.appendChild(feedbackLine)
    panel.appendChild(body)
    this.palette.appendChild(panel)
    this.palette.appendChild(this.buildCloseButtonBento())
  }

  private getIndexPaletteCopy(): { title: string, description: string } {
    if (this.indexPaletteState.rows?.length) {
      return {
        title: this.indexPaletteState.title ?? 'spaces',
        description: this.indexPaletteState.description ?? 'Tap once to select. Tap the same space again to open it.'
      }
    }
    if (this.indexPaletteState.status === 'empty') {
      return {
        title: this.indexPaletteState.title ?? 'empty',
        description: this.indexPaletteState.description ?? 'empty'
      }
    }
    if (this.indexPaletteState.status === 'unavailable') {
      return {
        title: this.indexPaletteState.title ?? 'unavailable',
        description: this.indexPaletteState.description ?? 'unavailable'
      }
    }
    return {
      title: this.indexPaletteState.title ?? 'blank',
      description: this.indexPaletteState.description ?? 'blank'
    }
  }

  private buildIndexList(): HTMLElement {
    const shell = document.createElement('div')
    const copy = this.getIndexPaletteCopy()
    shell.className = 'cmd-index-list'
    shell.setAttribute('role', 'region')
    shell.setAttribute('aria-label', 'Index spaces list ordered by name')
    this.indexListEl = shell
    this.indexListItems = []
    this.indexListSelectedIndex = -1

    const rows = this.indexPaletteState.rows ?? []
    if (rows.length) {
      const list = document.createElement('div')
      list.className = 'cmd-index-list-rows'
      rows.forEach((row) => {
        const item = document.createElement('div')
        item.className = 'cmd-index-list-row'
        item.dataset.rowId = row.id
        if (row.current) item.classList.add('is-current')
        if (row.current) item.classList.add('is-status-carrier')
        if (row.statusTone) item.classList.add(`is-status-${row.statusTone}`)
        item.setAttribute('role', 'button')
        item.tabIndex = 0
        const label = document.createElement('div')
        label.className = 'cmd-index-list-row-label'
        label.textContent = row.label
        item.appendChild(label)
        if (row.description) {
          const description = document.createElement('div')
          description.className = 'cmd-index-list-row-description'
          description.textContent = row.description
          item.appendChild(description)
        }
        const runRowAction = (): void => {
          this.pressThen(item, () => this.indexPaletteState.onOpenRow?.(row.id))
        }
        item.addEventListener('click', (e) => {
          e.preventDefault()
          e.stopPropagation()
          runRowAction()
        })
        item.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            e.stopPropagation()
            runRowAction()
          }
        })
        list.appendChild(item)
        this.indexListItems.push(item)
      })
      shell.appendChild(list)
      return shell
    }

    const list = document.createElement('div')
    list.className = 'cmd-index-list-rows'

    const infoRow = document.createElement('div')
    infoRow.className = 'cmd-index-list-row'
    const infoLabel = document.createElement('div')
    infoLabel.className = 'cmd-index-list-row-label'
    infoLabel.textContent = copy.description
    infoRow.appendChild(infoLabel)
    list.appendChild(infoRow)

    shell.appendChild(list)
    return shell
  }

  private configureIndexSlotPicker(): void {
    const actions = sortCommandListValues(this.indexPaletteState.actions ?? [])
    this.slotPicker = {
      label: 'pick/',
      options: [
        { label: 'index command list:', value: 'pick/' },
        ...actions.map(action => {
          const availability = this.indexPaletteState.resolveActionAvailability?.(action)
            ?? { state: 'armed' as const }
          const command = this.commands.get(action)
          return {
            label: action,
            value: action,
            group: action === 'index-export-all' ? 'library' : 'index',
            state: availability.state,
            gatedReason: availability.gatedReason,
            description: availability.description ?? (command ? this.getCommandDescription(command) : undefined)
          }
        })
      ],
      onSelect: (value) => this.indexPaletteState.onAction?.(value),
      closeOnSelect: false
    }
    this.wheelMode = 'slot'
  }

  private buildIndexMiddleStack(): void {
    if (!this.palette) return
    const stack = document.createElement('div')
    stack.className = 'cmd-index-middle-stack'
    stack.appendChild(this.buildIndexList())
    this.configureIndexSlotPicker()
    this.buildWheel(stack)
    this.palette.appendChild(stack)
  }

  private applyInitialIndexSelection(): void {
    if (!this.indexListItems.length) return
    const rows = this.indexPaletteState.rows ?? []
    const currentIndex = rows.findIndex((row) => row.current)
    const preferred = currentIndex !== -1 ? currentIndex : 0
    if (preferred === -1) {
      this.indexListSelectedIndex = -1
      this.updateIndexListSelectionUI()
      return
    }
    this.setIndexListSelectedIndex(preferred, { scroll: true, immediate: true, notify: false })
  }

  private setIndexListSelectedIndex(
    index: number,
    options: { scroll?: boolean, immediate?: boolean, notify?: boolean, direction?: number } = {}
  ): void {
    const length = this.indexListItems.length
    if (!length) {
      this.indexListSelectedIndex = -1
      return
    }
    const clamped = Math.min(Math.max(index, 0), length - 1)
    this.indexListSelectedIndex = clamped
    if (options.scroll) {
      this.scrollIndexListToIndex(clamped, options.immediate)
    }
    this.updateIndexListSelectionUI()
    if (options.notify) {
      const row = this.indexPaletteState.rows?.[clamped]
      if (row) {
        void this.indexPaletteState.onSelectRow?.(row.id)
      }
    }
  }

  private updateIndexListSelectionUI(): void {
    this.indexListItems.forEach((item, idx) => {
      item.classList.remove('is-selected')
      item.setAttribute('aria-selected', idx === this.indexListSelectedIndex ? 'true' : 'false')
    })
  }

  private scrollIndexListToIndex(index: number, immediate?: boolean): void {
    if (!this.indexListEl) return
    const item = this.indexListItems[index]
    if (!item) return
    const itemTop = item.offsetTop
    const itemHeight = item.offsetHeight
    const viewportHeight = this.indexListEl.clientHeight
    const centeredTarget = itemTop - Math.max(0, (viewportHeight - itemHeight) / 2)
    const target = Math.max(0, centeredTarget)
    if (this.indexListScrollReleaseTimer) {
      window.clearTimeout(this.indexListScrollReleaseTimer)
      this.indexListScrollReleaseTimer = undefined
    }
    if (immediate) {
      this.indexListEl.scrollTop = target
      return
    }
    if (typeof this.indexListEl.scrollTo === 'function') {
      this.indexListEl.scrollTo({ top: target, behavior: 'smooth' })
    } else {
      this.indexListEl.scrollTop = target
    }
    this.indexListScrollReleaseTimer = window.setTimeout(() => {
      this.indexListScrollReleaseTimer = undefined
    }, 200)
  }

  private shiftIndexListSelection(delta: number): void {
    if (!this.indexListItems.length) return
    const current = this.indexListSelectedIndex === -1 ? 0 : this.indexListSelectedIndex
    const target = Math.min(Math.max(current + delta, 0), this.indexListItems.length - 1)
    this.setIndexListSelectedIndex(target, { scroll: true, direction: delta })
    const row = this.indexPaletteState.rows?.[target]
    const item = this.indexListItems[target]
    if (row && item) {
      this.pressThen(item, () => this.indexPaletteState.onOpenRow?.(row.id))
    }
  }

  private handleExpandedCloseAction(): void {
    if (this.surface === 'update') {
      const quietChoice = this.noticeState?.choices.find(choice => choice.tone === 'quiet')
      if (quietChoice) {
        void quietChoice.onPick()
      } else {
        this.closePalette()
      }
      return
    }
    if (this.engineActive) {
      this.exitEngine()
      return
    }
    if (this.mode && this.modeExitHandler) {
      this.modeExitHandler(this.mode.name)
      return
    }
    this.hidePalette()
  }

  private getClosebarViewModel(): { label?: string, tone: 'accent' | 'neutral' } {
    if (this.surface === 'index') return { label: 'memory', tone: 'accent' }
    if (this.surface === 'sync') return { label: 'account', tone: 'accent' }
    if (this.surface === 'go') return { label: 'commands', tone: 'accent' }
    return { tone: 'neutral' }
  }

  private buildCloseButtonBento(): HTMLDivElement {
    const closebar = this.getClosebarViewModel()
    const row = document.createElement('div')
    row.className = 'cmd-runner-closebar'
    const button = document.createElement('button')
    button.type = 'button'
    button.className = `cmd-closebar-button is-${closebar.tone}`
    button.setAttribute('aria-label', 'Close expanded palette')
    if (closebar.label) {
      const label = document.createElement('span')
      label.className = 'cmd-closebar-label'
      label.textContent = closebar.label
      const separator = document.createElement('span')
      separator.className = 'cmd-closebar-separator'
      separator.setAttribute('aria-hidden', 'true')
      separator.textContent = '|'
      const close = document.createElement('span')
      close.className = 'cmd-closebar-close'
      close.textContent = 'close'
      button.appendChild(label)
      button.appendChild(separator)
      button.appendChild(close)
    } else {
      button.textContent = 'close'
    }
    button.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
      this.handleExpandedCloseAction()
    })
    row.appendChild(button)
    return row
  }

  private buildAccountBar(): void {
    if (!this.palette) return
    const topStack = document.createElement('div')
    topStack.className = 'cmd-top-bento-stack'

    const bar = document.createElement('div')
    bar.className = 'cmd-account'

    const user = document.createElement('div')
    user.className = 'cmd-account-chip cmd-account-user'
    this.fillAccountStatusLabel(user, this.getAccountNavigationLabel(this.accountUserLabel), this.accountUserTone, {
      onActionClick: (action) => this.handleAccountNavigationAction(action),
      isActionEnabled: (action) => action.trim().toLowerCase() === 'account'
    })
    user.classList.add('is-static')

    const space = document.createElement('div')
    space.className = 'cmd-account-chip cmd-account-space'
    this.fillAccountStatusLabel(space, this.accountSpaceLabel, this.accountSpaceTone, {
      onActionClick: this.accountSpaceHandler,
      isActionEnabled: this.accountSpaceActionEnabled
    })
    space.classList.add('is-static')

    bar.appendChild(user)
    bar.appendChild(space)
    topStack.appendChild(bar)
    this.palette.appendChild(topStack)
    this.accountBar = topStack
  }

  private updateAccountBar(): void {
    if (!this.accountBar) return
    const user = this.accountBar.querySelector('.cmd-account-user')
    const space = this.accountBar.querySelector('.cmd-account-space')
    if (user instanceof HTMLElement) {
      this.fillAccountStatusLabel(user, this.getAccountNavigationLabel(this.accountUserLabel), this.accountUserTone, {
        onActionClick: (action) => this.handleAccountNavigationAction(action),
        isActionEnabled: (action) => action.trim().toLowerCase() === 'account'
      })
    }
    if (space instanceof HTMLElement) {
      this.fillAccountStatusLabel(space, this.accountSpaceLabel, this.accountSpaceTone, {
        onActionClick: this.accountSpaceHandler,
        isActionEnabled: this.accountSpaceActionEnabled
      })
    }
  }

  private fillAccountStatusLabel(
    target: HTMLElement,
    label: string,
    tone: 'neutral' | 'sync' | 'offline' = 'neutral',
    opts?: {
      onActionClick?: (action?: string) => void
      isActionEnabled?: (action: string) => boolean
      renderActions?: boolean
    }
  ): void {
    target.textContent = ''
    const parsed = this.parseAccountStatusLabel(label)
    if (!parsed) {
      target.textContent = label
      return
    }
    const { key, value: valueText, actionsRaw, hasAtPrefix } = parsed

    const keyEl = document.createElement('span')
    keyEl.className = 'cmd-account-key'
    keyEl.textContent = key
    target.appendChild(keyEl)
    target.appendChild(this.createInlineSeparatorToken(':'))
    if (hasAtPrefix) {
      const at = document.createElement('span')
      at.className = 'cmd-account-at'
      at.textContent = '@'
      target.appendChild(at)
    }
    const value = document.createElement('span')
    value.className = 'cmd-account-value'
    if (tone === 'sync') value.classList.add('is-sync')
    if (tone === 'offline') value.classList.add('is-offline')
    value.textContent = valueText
    // Global rule: account/sync value text is informational; only the trailing action token is interactive.
    target.appendChild(value)

    if (actionsRaw.length > 0 && opts?.renderActions !== false) {
      target.appendChild(this.createInlineSeparatorToken('|'))
      const actions = actionsRaw
        .split('|')
        .map(part => part.trim())
        .filter(part => part.length > 0)
      if (!actions.length) return
      actions.forEach((actionToken, index) => {
        if (index > 0) {
          target.appendChild(this.createInlineSeparatorToken('|'))
        }
        const action = document.createElement('span')
        action.className = 'cmd-account-action'
        action.textContent = actionToken
        const isEnabled = opts?.isActionEnabled ? opts.isActionEnabled(actionToken) : true
        if (opts?.onActionClick && isEnabled) {
          action.classList.add('cmd-account-action-actionable')
          action.setAttribute('role', 'button')
          action.tabIndex = 0
          action.addEventListener('click', (e) => {
            e.preventDefault()
            e.stopPropagation()
            void opts.onActionClick?.(actionToken)
          })
          action.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              e.stopPropagation()
              void opts.onActionClick?.(actionToken)
            }
          })
        }
        target.appendChild(action)
      })
    }
  }

  private fillSyncStateLabel(target: HTMLElement, label?: string): void {
    target.textContent = ''
    if (!label) return
    let prefix = ''
    let rawValue = ''
    const slashIdx = label.indexOf('/')
    if (slashIdx !== -1) {
      prefix = label.slice(0, slashIdx).trim()
      rawValue = label.slice(slashIdx + 1).trim()
    } else {
      const colonSep = ' : '
      const colonIdx = label.indexOf(colonSep)
      if (colonIdx === -1) {
        target.textContent = label
        return
      }
      prefix = label.slice(0, colonIdx).trim()
      rawValue = label.slice(colonIdx + colonSep.length).trim()
    }
    if (!prefix || !rawValue) {
      target.textContent = label
      return
    }
    target.appendChild(document.createTextNode(`${prefix} : `))
    const separator = ' : '
    const separatorIdx = rawValue.indexOf(separator)
    const stateToken = separatorIdx === -1 ? rawValue : rawValue.slice(0, separatorIdx).trim()
    const trailing = separatorIdx === -1 ? '' : rawValue.slice(separatorIdx + separator.length).trim()

    const state = document.createElement('span')
    state.className = 'cmd-sync-state-row'
    if (stateToken === 'connected' || stateToken === 'active' || stateToken === 'online') {
      state.classList.add('is-connected')
    } else if (stateToken === 'local' || stateToken === 'off') {
      state.classList.add('is-local')
    } else if (stateToken === 'ending') {
      state.classList.add('is-ending')
    } else if (stateToken === 'deactivated') {
      state.classList.add('is-deactivated')
    } else if (stateToken === 'paused') {
      state.classList.add('is-paused')
    } else if (stateToken === 'offline' || stateToken === 'failed auth') {
      state.classList.add('is-offline')
    } else {
      state.classList.add('is-paused')
    }
    state.textContent = stateToken
    target.appendChild(state)

    if (trailing) {
      target.appendChild(document.createTextNode(` : ${trailing}`))
    }
  }

  private fillSyncDetailLabel(target: HTMLElement, label?: string): void {
    target.textContent = ''
    if (!label) return
    const separator = ' : '
    const separatorIdx = label.indexOf(separator)
    if (separatorIdx === -1) {
      target.textContent = label
      return
    }
    const prefix = label.slice(0, separatorIdx).trim()
    const value = label.slice(separatorIdx + separator.length).trim()
    if (!prefix || !value) {
      target.textContent = label
      return
    }
    target.appendChild(document.createTextNode(`${prefix} : `))
    const valueEl = document.createElement('span')
    valueEl.className = 'cmd-sync-detail-value'
    valueEl.textContent = value
    target.appendChild(valueEl)
  }

  enterMode(name: string, message: string, label?: string, activeTokens?: string[]): void {
    // Multi-step command pattern:
    //
    // 1. BEFORE calling enterMode, call setModeTokenHandler with your handler function:
    //    this.commands.setModeTokenHandler((token) => this.handleYourCommandTokenTap(token))
    //
    // 2. Build breadcrumb label with ALL steps (e.g., "command / step1 / step2 / done")
    //    - Separate tokens with ' / ' (space-slash-space)
    //    - First token is the command name
    //    - Include all steps in sequence, even if not yet reached
    //
    // 3. Pass activeTokens array with currently active/clickable tokens (forward-only for Phase 1):
    //    - Active tokens get underlined (visual focus indicator) and are clickable
    //    - Completed tokens are not clickable (back-nav deferred)
    //
    // 4. Token handler pattern:
    //    - Check which token was clicked
    //    - If it's a previous step: clear subsequent data, reset to that step
    //    - If it's current step: perform step action (like 'done' exits mode)
    //    - Call setModeLabel() to update breadcrumb + active tokens
    //    - Call updateModeMessage() to update status text
    //
    // 5. Navigation flow:
    //    - Click on active token → handler performs action for that step
    //    - X button / ESC → calls modeExitHandler, exits entire mode
    //
    // See connect (app.ts) for the reference implementation.
    this.mode = { name, message, label, activeTokens }
    if (this.outsideHandler) {
      document.removeEventListener('pointerdown', this.outsideHandler, true)
      this.outsideHandler = undefined
    }
    if (!this.isVisible) {
      this.showPalette()
      return
    }
    if (this.palette) {
      this.palette.classList.add('is-minimized')
    }
    this.applyModeStatus()
  }

  setModeAction(_action?: { label: string, bold?: boolean, onClick: () => void }): void {
    // Deprecated: Mode actions replaced by clickable breadcrumb tokens.
    // Use setModeTokenHandler + activeTokens pattern instead.
    // See enterMode documentation above for full pattern.
  }

  updateModeMessage(message: string): void {
    if (!this.mode) return
    this.mode.message = message
    this.applyModeStatus()
  }

  setModeLabel(label?: string, activeTokens?: string[]): void {
    if (!this.mode) return
    this.mode.label = label
    this.mode.activeTokens = activeTokens
    this.applyModeStatus()
  }

  exitMode(name?: string): void {
    if (!this.mode) return
    if (name && this.mode.name !== name) return
    this.mode = undefined
    this.modeTokenHandler = undefined
    if (this.promptCommandEl) {
      this.promptCommandEl.classList.remove('is-static')
    }
    this.slotPicker = undefined
    this.wheelMode = 'commands'
    // Let hidePalette() handle all cleanup including is-minimized removal
    this.hidePalette()
  }

  isModeActive(name?: string): boolean {
    if (!this.mode) return false
    return name ? this.mode.name === name : true
  }

  setStatus(message: string, tone: 'success' | 'error'): void {
    this.showRunnerResult(message, tone)
  }

  setSyncVisual(state: SyncVisualState): void {
    if (!this.syncIndicator) return
    const account = state === 'idle-disabled'
      ? 'none'
      : (state === 'blocked' ? 'offline' : 'online')
    this.setOrbitalVisuals({
      memory: state === 'idle-disabled' ? 'anonymous' : 'account',
      account
    })
  }

  setIndexVisual(state: SyncVisualState, progress = 0): void {
    if (!this.indexIndicator) return
    void progress
    this.indexIndicator.classList.remove('is-world-anonymous', 'is-world-account')
    this.indexIndicator.classList.add(state === 'idle-disabled' ? 'is-world-anonymous' : 'is-world-account')
  }

  setOrbitalVisuals(state: OrbitalVisualState): void {
    if (this.palette) {
      this.palette.classList.remove(
        'is-memory-anonymous',
        'is-memory-account',
        'is-account-none',
        'is-account-online',
        'is-account-offline',
        'is-account-deactivated'
      )
      this.palette.classList.add(state.memory === 'account' ? 'is-memory-account' : 'is-memory-anonymous')
      this.palette.classList.add(`is-account-${state.account}`)
    }
    if (this.indexIndicator) {
      const shouldRestartMemorySettle =
        state.memoryActivity === 'settling'
        && state.memoryActivityKey !== undefined
        && state.memoryActivityKey !== this.memoryActivityKey
      this.memoryActivityKey = state.memoryActivityKey
      this.indexIndicator.classList.remove(
        'is-world-anonymous',
        'is-world-account',
        'is-memory-settling',
        'is-idle-synced',
        'is-idle-disabled',
        'is-pending',
        'is-syncing',
        'is-success',
        'is-blocked'
      )
      this.indexIndicator.classList.add(state.memory === 'account' ? 'is-world-account' : 'is-world-anonymous')
      if (shouldRestartMemorySettle) {
        void this.indexIndicator.offsetWidth
      }
      this.indexIndicator.classList.toggle('is-memory-settling', state.memoryActivity === 'settling')
    }
    if (this.syncIndicator) {
      this.syncIndicator.classList.remove(
        'is-account-none',
        'is-account-online',
        'is-account-offline',
        'is-account-deactivated',
        'is-account-debt',
        'is-account-activity-running',
        'is-account-activity-blocked',
        'is-idle-synced',
        'is-idle-disabled',
        'is-pending',
        'is-syncing',
        'is-success',
        'is-blocked'
      )
      this.syncIndicator.classList.add(`is-account-${state.account}`)
      this.syncIndicator.classList.toggle('is-account-debt', state.accountActivity !== undefined)
      this.syncIndicator.classList.toggle('is-account-activity-running', state.accountActivity === 'running')
      this.syncIndicator.classList.toggle('is-account-activity-blocked', state.accountActivity === 'blocked')
    }
  }

  // Backward compatibility during transition.
  setSyncIndicator(status: 'idle' | 'pending' | 'syncing' | 'success' | 'error'): void {
    if (status === 'idle') {
      this.setSyncVisual('idle-synced')
      return
    }
    if (status === 'pending') {
      this.setSyncVisual('pending')
      return
    }
    if (status === 'syncing') {
      this.setSyncVisual('syncing')
      return
    }
    if (status === 'success') {
      this.setSyncVisual('success')
      return
    }
    this.setSyncVisual('blocked')
  }

  setSyncIndicatorVisible(isVisible: boolean): void {
    if (!this.syncIndicator) return
    this.syncIndicator.classList.toggle('is-hidden', !isVisible)
  }

  setSpaceKind(space: SpaceKind): void {
    if (this.spaceKind === space) return
    this.spaceKind = space
    if (!this.palette) return
    this.palette.classList.toggle('is-index-context', isDetachedSpace(this.spaceKind))
    if (!this.isVisible) {
      this.renderCollapsedPaletteChrome(this.palette)
      return
    }
    if (this.surface === 'go') {
      this.hidePalette('command')
    }
  }

  // Backward compatibility during the index-to-palette migration.
  setIndexContext(isIndex: boolean): void {
    this.setSpaceKind(isIndex ? 'detached' : 'authored')
  }

  clearStatus(): void {
    this.setFeedback()
  }

  minimizePalette(): void {
    if (!this.palette || !this.isVisible) return
    // Toggle the minimized state so repeated calls do nothing; runner stays visible.
    if (!this.palette.classList.contains('is-minimized')) {
      this.palette.classList.add('is-minimized')
    }
  }

  openSlotPicker(picker: SlotPicker): void {
    this.slotPicker = picker
    this.slotPickerTapValue = undefined
    this.wheelMode = 'slot'
    if (!this.isVisible) {
      this.showPalette()
      return
    }
    if (this.palette) {
      this.palette.classList.remove('is-minimized')
    }
    this.renderPaletteContents()
  }

  closeSlotPicker(opts?: { minimize?: boolean }): void {
    this.slotPicker = undefined
    this.wheelMode = 'commands'
    this.renderPaletteContents()
    if (opts?.minimize && this.mode && this.palette) {
      this.palette.classList.add('is-minimized')
    }
  }

  setSlotSelectionByValue(value: string): void {
    if (!this.slotPicker) return
    const idx = this.slotPicker.options.findIndex(opt => opt.value === value)
    if (idx === -1) return
    this.setSelectedIndex(idx, { scroll: true, immediate: true })
    this.lastHighlightedSlotIndex = idx
  }

  openPickerStep(opts: { label: string, options: SlotOption[], defaultValue?: string, onSelect: (value: string) => void | Promise<void>, onConfirm?: () => void | Promise<void>, onHighlight?: (value: string) => void, closeOnSelect?: boolean }): void {
    this.slotPicker = {
      label: opts.label,
      options: opts.options,
      onSelect: opts.onSelect,
      onConfirm: opts.onConfirm,
      onHighlight: opts.onHighlight,
      closeOnSelect: opts.closeOnSelect ?? false,
      previewOnHighlight: true
    }
    this.slotPickerTapValue = undefined
    this.wheelMode = 'slot'
    if (!this.isVisible) {
      this.showPalette()
    } else if (this.palette) {
      this.palette.classList.remove('is-minimized')
    }
    this.renderPaletteContents()
    const value = opts.defaultValue ?? opts.options[0]?.value
    if (value) {
      this.setSlotSelectionByValue(value)
    }
  }

  closePalette(): void {
    this.applyPaletteTransition({ overlay: 'collapsed' })
  }

  ensurePaletteVisible(): void {
    if (!this.isVisible) {
      this.showPalette()
      return
    }
    if (this.mode) return
    if (this.palette) {
      this.palette.classList.remove('is-minimized')
    }
  }

  isKeyboardCapturing(): boolean {
    return this.isVisible
  }

  private getCollapsedPillLabel(): string {
    return isDetachedSpace(this.spaceKind) ? 'index' : 'Go'
  }

  private renderCollapsedPaletteChrome(palette: HTMLDivElement): void {
    palette.innerHTML = ''
    palette.setAttribute('role', 'button')
    palette.setAttribute('aria-expanded', 'false')
    if (isDetachedSpace(this.spaceKind)) {
      palette.setAttribute('aria-label', 'Index palette unavailable')
    } else {
      palette.setAttribute('aria-label', 'Open command palette')
    }
    const pillShell = document.createElement('span')
    pillShell.className = 'cmd-pill-shell'
    const pillLabel = document.createElement('span')
    pillLabel.className = 'cmd-pill-label'
    pillLabel.textContent = this.getCollapsedPillLabel()
    pillShell.appendChild(pillLabel)
    palette.appendChild(pillShell)
    if (this.syncIndicator || this.indexIndicator) {
      const affordances = document.createElement('div')
      affordances.className = 'cmd-affordances'
      if (this.indexIndicator) {
        affordances.appendChild(this.indexIndicator)
      }
      if (this.syncIndicator) {
        affordances.appendChild(this.syncIndicator)
      }
      palette.appendChild(affordances)
    }
  }

  private renderEngineRunnerState(state: RunnerState): void {
    if (!this.promptLabelEl || !this.promptCommandEl || !this.promptStepEl || !this.promptButton) return
    const promptLabelEl = this.promptLabelEl
    const promptCommandEl = this.promptCommandEl
    const promptStepEl = this.promptStepEl
    const promptButton = this.promptButton
    promptButton.classList.add('is-engine')
    promptButton.classList.remove('is-placeholder')
    promptButton.classList.remove('is-command')
    promptLabelEl.textContent = ''
    const commandToken = state.tokens.find(token => token.kind === 'command')
    promptCommandEl.textContent = commandToken?.label ?? ''
    const inputTokenId = state.input?.tokenId
    const inputValue = state.input?.value ?? ''
    const signature = JSON.stringify(state.tokens.map(token => [
      token.id, token.kind, token.label, token.id === inputTokenId ? null : token.value
    ]))
    const existingInput = promptStepEl.querySelector<HTMLInputElement>('input.cmd-runner-input')
    const keepInput = Boolean(inputTokenId && existingInput && this.engineTokenSignature === signature)
    this.engineTokenSignature = signature
    let newInput: HTMLInputElement | undefined

    if (!keepInput) {
      promptStepEl.textContent = ''

      state.tokens.forEach((token) => {
        if (token.kind === 'command') return
        const span = document.createElement('span')
        span.className = 'cmd-runner-step-text'
        span.style.whiteSpace = token.id === inputTokenId ? 'normal' : 'nowrap'
        if (token.id === inputTokenId) {
          span.classList.add('cmd-runner-input-token')
        }
        const content = document.createElement('span')
        content.className = 'cmd-runner-token-content'
        if (token.label) {
          const label = document.createElement('span')
          label.className = 'cmd-runner-token-label'
          label.textContent = token.label
          content.appendChild(label)
        }
        if (token.id === inputTokenId) {
          if (token.label) {
            const valuePrefix = document.createElement('span')
            valuePrefix.className = 'cmd-runner-separator'
            valuePrefix.textContent = ' : '
            content.appendChild(valuePrefix)
          }
          const input = document.createElement('input')
          input.type = 'text'
          input.className = 'cmd-runner-input'
          input.value = inputValue
          input.setAttribute('aria-label', token.label || `${commandToken?.label ?? 'command'} query`)
          input.addEventListener('input', (e) => {
            const target = e.target as HTMLInputElement
            this.engine?.handleTextInput(target.value)
          })
          input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
              e.stopPropagation()
              if (e.isComposing) return
              e.preventDefault()
              this.runEngineAction()
            }
          })
          content.appendChild(input)
          newInput = input
        } else if (token.value && token.value.length) {
          const valuePrefix = document.createElement('span')
          valuePrefix.className = 'cmd-runner-separator'
          valuePrefix.textContent = ' : '
          content.appendChild(valuePrefix)
          const value = document.createElement('span')
          value.className = 'cmd-runner-token-value'
          value.textContent = token.value
          content.appendChild(value)
        }
        span.appendChild(content)

        if (token.id !== inputTokenId) {
          span.dataset.token = token.id
          // A pointer press must not dismiss the keyboard before an action succeeds.
          span.addEventListener('pointerdown', (e) => {
            if (this.engineRunnerState?.input) e.preventDefault()
          })
          span.addEventListener('click', (e) => {
            e.preventDefault()
            e.stopPropagation()
            if (span.getAttribute('role') !== 'button' || span.getAttribute('aria-disabled') === 'true') return
            this.runEngineAction(token.id)
          })
          span.addEventListener('keydown', (e) => {
            if (e.key !== 'Enter' && e.key !== ' ') return
            e.stopPropagation()
            if (e.isComposing) return
            e.preventDefault()
            if (span.getAttribute('role') !== 'button' || span.getAttribute('aria-disabled') === 'true') return
            this.runEngineAction(token.id)
          })
        }

        promptStepEl.appendChild(span)
      })
    }

    // Patch availability in place: changing the hit count must not replace a
    // focused (or deliberately blurred) query field or action token.
    state.tokens.filter(token => token.kind !== 'command').forEach((token, index) => {
      const span = promptStepEl.children[index] as HTMLElement
      const actionable = token.active && token.id !== inputTokenId
      span.querySelector('.cmd-runner-token-content')?.classList.toggle('cmd-runner-token-active', actionable)
      if (token.id === inputTokenId) return
      if (actionable || token.kind === 'action') {
        span.setAttribute('role', 'button')
        span.setAttribute('aria-disabled', String(!actionable))
        span.tabIndex = actionable ? 0 : -1
      } else {
        span.removeAttribute('role')
        span.removeAttribute('aria-disabled')
        span.removeAttribute('tabindex')
      }
    })
    if (keepInput && existingInput && document.activeElement !== existingInput && existingInput.value !== inputValue) {
      existingInput.value = inputValue
    }
    this.setFeedback(state.feedback?.message, state.feedback?.tone)
    if (this.descriptionNameEl) this.descriptionNameEl.textContent = ''
    if (this.descriptionDashEl) this.descriptionDashEl.style.display = 'none'
    if (this.descriptionCopyEl) this.descriptionCopyEl.textContent = state.instruction
    if (this.promptDescriptionBodyEl && !state.instruction) this.promptDescriptionBodyEl.style.display = 'none'

    if (state.input) {
      promptButton.disabled = false
      promptButton.classList.remove('is-armed')
    } else if (this.engineHasDone) {
      promptButton.disabled = false
      promptButton.classList.add('is-armed')
    } else {
      promptButton.disabled = true
      promptButton.classList.remove('is-armed')
    }
    newInput?.focus()
  }

  private runEngineAction(tokenId?: string): void {
    const input = this.promptStepEl?.querySelector<HTMLInputElement>('input.cmd-runner-input')
    const accepted = tokenId ? this.engine?.handleTokenTap(tokenId) : this.engine?.handleEnter()
    if (accepted) input?.blur()
  }

  private setPromptLabel(label: string, activeTokens?: string[]): void {
    const commandEl = this.promptCommandEl
    const stepEl = this.promptStepEl
    if (!commandEl || !stepEl) return
    const parts = label.split(' / ')
    const base = parts.shift() ?? ''
    const stepTokens = parts

    const activeSet = new Set(activeTokens ?? [])
    const isPlaceholder = !base && stepTokens.length === 0
    this.promptButton?.classList.toggle('is-placeholder', isPlaceholder)
    this.promptButton?.classList.toggle('is-command', !isPlaceholder && Boolean(base))
    if (this.promptLabelEl && isPlaceholder) {
      this.promptLabelEl.textContent = ''
    }
    commandEl.textContent = base
    commandEl.classList.remove('cmd-runner-token-active')
    stepEl.textContent = ''

    const makeClickable = (span: HTMLSpanElement, token: string) => {
      const isActive = activeSet.has(token)
      if (isActive) {
        span.classList.add('cmd-runner-token-active')
      }
      if (this.modeTokenHandler && isActive) {
        span.dataset.token = token
        span.tabIndex = 0
        span.setAttribute('role', 'button')
        span.addEventListener('click', (e) => {
          e.preventDefault()
          e.stopPropagation()
          if ((e as any).stopImmediatePropagation) {
            (e as any).stopImmediatePropagation()
          }
          this.modeTokenHandler?.(token)
        })
        span.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            e.stopPropagation()
            if ((e as any).stopImmediatePropagation) {
              (e as any).stopImmediatePropagation()
            }
            this.modeTokenHandler?.(token)
          }
        })
      }
    }

    stepTokens.forEach((token) => {
      const tokenParts = token.split('|').map(t => t.trim()).filter(Boolean)
      if (!tokenParts.length) return
      const baseToken = tokenParts[0]
      const span = document.createElement('span')
      span.className = 'cmd-runner-step-text'
      span.textContent = `/${baseToken}`
      span.style.whiteSpace = 'nowrap'
      makeClickable(span, baseToken)

      if (tokenParts.length > 1) {
        const doneToken = tokenParts[1]
        const doneSpan = document.createElement('span')
        doneSpan.className = 'cmd-runner-step-text'
        doneSpan.textContent = ` | ${doneToken}`
        doneSpan.style.whiteSpace = 'nowrap'
        makeClickable(doneSpan, doneToken)
        span.appendChild(doneSpan)
      }
      stepEl.appendChild(span)
    })

    if (!stepTokens.length && base && (!activeTokens || activeSet.has(base))) {
      commandEl.classList.add('cmd-runner-token-active')
    }
  }

  private getPromptPrefix(): string {
    if (this.surface === 'sync') return 'account'
    if (this.surface === 'index') return 'index'
    return 'go'
  }

  private setFeedback(message?: string, tone?: 'success' | 'error'): void {
    if (!this.promptFeedbackEl) return
    this.promptFeedbackEl.classList.remove('is-success', 'is-error')
    this.promptFeedbackEl.textContent = ''
    const line = this.promptFeedbackEl.parentElement as HTMLElement | null
    const descriptionBody = this.promptDescriptionBodyEl
    if (!message) {
      if (line) line.style.display = 'none'
      if (descriptionBody) descriptionBody.style.display = ''
      return
    }
    if (tone) {
      this.promptFeedbackEl.classList.add(tone === 'success' ? 'is-success' : 'is-error')
    }
    this.promptFeedbackEl.textContent = `> ${message}`
    if (line) line.style.display = 'flex'
    if (descriptionBody) descriptionBody.style.display = 'none'
  }
}
